/**
 * Main Web Studio namespace
 * @exports src/client/facade
 * @namespace WS
 */
// Engine
import events from "_src/engine/events";
import output from "_src/engine/output";
import {
  easingType,
  pixelBufferType,
  textureMagFilter,
  textureMinFilter,
} from "_src/engine/constants";
import memoryProfiler from "_src/engine/memory_profiler";
import GL from "_src/engine/gl";
import renderer from "_src/engine/renderer";
import extensions from "_src/engine/extensions";
import materialFactory from "_src/client/materials/material_factory";
import controlFactory from "_src/client/controls/control_factory";
import minmaxUtils from "_src/engine/utility/minmax";
import {BufferReaderType} from "_src/client/buffer_reader";

import {vec3, quat, mat4} from "_src/libs/gl-matrix";
import loader from "_src/loader";
import reformer from "_src/reformer/reformer";

import {
  msgType,
  objectType,
  basePlane,
  postEffects,
  stepType,
  fittingMode,
  cadPrimitive,
  cadType, photoState, scanMode
} from "_src/constants";
import { colorUtils } from "_src/utils";
import renderGraph from "_src/render_graph/render_graph";
import workspace from "_src/workspace";
import history from "_src/history/manager";
import config from "_src/config";
import appState from "_src/app_state";
import objState from "_src/obj_state";
import commonAPI from "_src/common_api";

// Client-only modules
import windowManager from "_src/client/window_manager";
import bbox from "_src/client/bbox";
import viewcube from "_src/client/viewcube";
import grid from "_src/client/grid";
import normals from "_src/client/normals";
import mirror from "_src/client/mirror";
import Animation from "_src/client/animation";
import cache from "_cacheModule";
import indexedDataBase from "_src/client/database";
import selectionWidgets from "_src/client/widgets/selection_shapes";
import textWidget from "_src/client/widgets/text";
import {screenUtils} from "_src/client/utils";
import gizmo from "_src/client/controls/gizmo";
import hud from "_src/client/hud";
import textureAdjustment from "_src/client/texture_adjustment";
import controlUtils from "_src/client/controls/utils";
import annotationsModule from "_src/client/annotations";
import inputCollector from "_src/client/input";
import decorator from "_src/client/decorator";
import measurements from "_src/client/measurements";
import photos from "_src/client/photos";

// Manipulators
import View from "_src/client/manipulators/view";
import Align from "_src/client/manipulators/align";
import Eraser from "_src/client/manipulators/eraser";
import CadFitter from "_src/client/manipulators/cadfitter";
import Smoother from "_src/client/manipulators/smoother";
import TransformManipulator from "_src/client/manipulators/transform";
import Positioner from "_src/client/manipulators/positioner";
import Measurements from "_src/client/manipulators/measurements"
import Defeature from "_src/client/manipulators/defeature";
import TextureHealer from "_src/client/manipulators/texturehealer";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import {cleanupRotationIndicator} from "_src/client/widgets/rotation_indicator";
import preview from "_src/client/preview";
import commands from "_src/manipulators/commands";
import {commonFacadeAPI, commonFacadeFunctions} from "_src/common_facade";
import CadPicker from "_src/client/manipulators/cadpicker";
import {OBJECT_HIGHLIGHT} from "_src/render_graph/passes/highlight_pass";
import {clearMeasureLines, initMeasureLines} from "_src/client/widgets/measure_line";
import {clearCadAxis, initCadAxis} from "_src/client/widgets/cad_axis";
import loadingProgress from "_src/loading_progress";

import packageJson from "_packageJson";
import monitoring from "_src/monitoring";
import {initZSTDWorkers} from "_src/engine/loaders/zstd/zstd_loader";
import projectTree from "_src/reformer/project_tree";
const VERSION = packageJson.version;

const animation = new Animation();

let _highlightedObject = null;
const _mirroringState = {
  enabled: false,
  point: null,
  plane: null,
};

const _objectPreviewTasks = [];
let _activePreviewTask = null;

const _selectedGeometries = new Set();
const _alignWindowToObjects = new WeakMap();

const DRAW_QUERIES_FINE = -1;
const DRAW_QUERIES_SHOULD_STOP = 0;
const DRAW_QUERIES_SHOULD_RUN  = 1;

let _drawQueriesState = DRAW_QUERIES_FINE;
let _prevQueriesState = DRAW_QUERIES_FINE;

let viewManipulator = null;
let alignManipulator = null;
let eraseManipulator = null;
let cadFitterManipulator = null;
let cadPickerManipulator = null;
let smoothManipulator = null;
let transformManipulator = null;
let positionManipulator = null;
let measurementsManipulator = null;
let defeatureManipulator = null;
let textureHealerManipulator = null;

const defaultMaxHeapBytesize = 500000000;

let controlIsActive = false;

//Note: this is debug container for serializing commands sending to nodejs
//const commandStack = [];

const WS_DERIVED = {
  /** Library version (same as in package.json)
   * @memberof WS
   * @member version
   */
  version: VERSION,

  /** Wireframe Type. Object will be drawn transparent with opaque wireframe
   * @memberof WS
   * @member WT_TRANSPARENT
   */
  WT_TRANSPARENT: appState.WT_TRANSPARENT,

  /** Wireframe Type. Both object and wireframe will be drawn opaque
   * @memberof WS
   * @member WT_OPAQUE
   */
  WT_OPAQUE: appState.WT_OPAQUE,

  /** Tool Type None
   * @memberof WS
   * @member TT_NONE
   */
  TT_NONE: appState.TT_NONE,

  /** Tool Type View
   * @memberof WS
   * @member TT_VIEW
   */
  TT_VIEW: appState.TT_VIEW,

  /** Tool Type Align
   * @memberof WS
   * @member TT_ALIGN
   */
  TT_ALIGN: appState.TT_ALIGN,

  /** Tool Type Eraser
   * @memberof WS
   * @member TT_ERASER
   */
  TT_ERASER: appState.TT_ERASER,

  /** Tool Type Smoother
   * @memberof WS
   * @member TT_SMOOTHER
   */
  TT_SMOOTHER: appState.TT_SMOOTHER,

  /** Tool Type Transform
   * @memberof WS
   * @member TT_TRANSFORM
   */
  TT_TRANSFORM: appState.TT_TRANSFORM,

  /** Tool Type Positioner
   * @memberof WS
   * @member TT_POSITIONER
   */
  TT_POSITIONER: appState.TT_POSITIONER,

  /** Tool Type Measurements
   * @memberof WS
   * @member TT_MEASUREMENTS
   */
  TT_MEASUREMENTS: appState.TT_MEASUREMENTS,

  /** Tool Type CadFitter
   * @memberof WS
   * @member TT_CADFITTER
   */
  TT_CADFITTER: appState.TT_CADFITTER,

  /** Tool Type CadPicker
   * @memberof WS
   * @member TT_CADPICKER
   */
  TT_CADPICKER: appState.TT_CADPICKER,

  /** Tool Type Defeature
   * @memberof WS
   * @member TT_DEFEATURE
   */
  TT_DEFEATURE: appState.TT_DEFEATURE,

  /** Tool Type TextureHealer
   * @memberof WS
   * @member TT_TEXTURE_HEALER
   */
  TT_TEXTURE_HEALER: appState.TT_TEXTURE_HEALER,

  /** Selection Mode Select
   * @memberof WS
   * @member SM_SELECT
   */
  SM_SELECT: appState.SM_SELECT,

  /** Selection Mode Unselect
   * @memberof WS
   * @member SM_UNSELECT
   */
  SM_UNSELECT: appState.SM_UNSELECT,

  /** Tool Type LOD View
   * @memberof WS
   * @member TT_LOD
   */
  TT_LOD: appState.TT_LOD,

  /** Orthogonal camera type
   * @memberof WS
   * @member CT_ORTHO
   */
  CT_ORTHO: appState.CT_ORTHO,

  /** Perspective camera type
   * @memberof WS
   * @member CT_PERSPECTIVE
   */
  CT_PERSPECTIVE: appState.CT_PERSPECTIVE,

  /** Camera control "trackball"
   * @memberof WS
   * @member CC_TRACKBALL
   */
  CC_TRACKBALL: appState.CC_TRACKBALL,

  /** Camera control "orbit"
   * @memberof WS
   * @member CC_ORBIT
   */
  CC_ORBIT: appState.CC_ORBIT,

  /** Selection type "Sphere"
   * @memberof WS
   * @member ST_SPHERE
   */
  ST_SPHERE: appState.ST_SPHERE,

  /** Selection type "Circle"
   * @memberof WS
   * @member ST_CIRCLE
   */
  ST_CIRCLE: appState.ST_CIRCLE,

  /** Selection type "Rectangle"
   * @memberof WS
   * @member ST_RECTANGLE
   */
  ST_RECTANGLE: appState.ST_RECTANGLE,

  /** Selection type "Lasso"
   * @memberof WS
   * @member ST_LASSO
   */
  ST_LASSO: appState.ST_LASSO,

  /** Selection type "Cut off plane"
   * @memberof WS
   * @member ST_CUT_OFF_PLANE
   */
  ST_CUT_OFF_PLANE: appState.ST_CUT_OFF_PLANE,

  /** Selection type "None". Means that selection is not active.
   * @memberof WS
   * @member ST_NONE
   */
  ST_NONE: appState.ST_NONE,

  /** Objects color mode "Frame color"
   * @memberof WS
   * @member CM_FRAME_COLOR
   */
  CM_FRAME_COLOR: appState.CM_FRAME_COLOR,

  /** Objects color mode "Max error"
   * @memberof WS
   * @member CM_MAX_ERROR
   */
  CM_MAX_ERROR: appState.CM_MAX_ERROR,

  /** Objects color mode "X-ray"
   * @memberof WS
   * @member CM_XRAY
   */
  CM_XRAY: appState.CM_XRAY,

  /** Objects backface mode "None"
   * @memberof WS
   * @member BM_NONE
   */
  BM_NONE: appState.BM_NONE,

  /** Objects backface mode "DIFFUSE"
   * @memberof WS
   * @member BM_DIFFUSE
   */
  BM_DIFFUSE: appState.BM_DIFFUSE,

  /** Objects backface mode "FRONTFACE"
   * @memberof WS
   * @member BM_FRONTFACE
   */
  BM_FRONTFACE: appState.BM_FRONTFACE,

  /** Objects backface mode "TRANSPARENT"
   * @memberof WS
   * @member BM_TRANSPARENT
   */
  BM_TRANSPARENT: appState.BM_TRANSPARENT,

  /** Surfels filtering is disabled
   * @memberof WS
   * @member SM_NONE
   */
  SM_NONE: appState.SM_NONE,

  /** Surfels filtering mode "Concurrent"
   * @memberof WS
   * @member SM_CONCURRENT
   */
  SM_CONCURRENT: appState.SM_CONCURRENT,

  /** Surfels filtering mode "Blend"
   * @memberof WS
   * @member SM_BLEND
   */
  SM_BLEND: appState.SM_BLEND,

  /** Objects material "Astudio"
   * @memberof WS
   * @member OM_ASTUDIO
   */
  OM_ASTUDIO: materialFactory.ASTUDIO,

  /** Objects material "MatCap"
   * @memberof WS
   * @member OM_MATCAP
   */
  OM_MATCAP: materialFactory.MATCAP,

  /** Objects material "PBR"
   * @memberof WS
   * @member OM_PBR
   */
  OM_PBR: materialFactory.PBR,

  /** Object type "Scan"
   * @memberof WS
   * @member MT_SCAN
   */
  MT_SCAN: objectType.SCAN,

  /** Object type "Fusion"
   * @memberof WS
   * @member MT_FUSION
   */
  MT_FUSION: objectType.FUSION,

  /** Object type "CAD"
   * @memberof WS
   * @member MT_CAD
   */
  MT_CAD: objectType.CAD,

  /** Object type "Group"
   * @memberof WS
   * @member MT_GROUP
   */
  MT_GROUP: objectType.GROUP,

  /** Object type "Point Cloud"
   * @memberof WS
   * @member MT_POINTCLOUD
   */
  MT_POINTCLOUD: objectType.POINTCLOUD,

  /** Object type "Photo Group"
   * @memberof WS
   * @member MT_PHOTO_GROUP
   */
  MT_PHOTO_GROUP: objectType.PHOTO_GROUP,

  /** Shading type "Smooth"
   * @memberof WS
   * @member SHT_SMOOTH
   */
  SHT_SMOOTH: appState.SHT_SMOOTH,

  /** Shading type "Flat"
   * @memberof WS
   * @member SHT_FLAT
   */
  SHT_FLAT: appState.SHT_FLAT,

  /** Error related to project loading process
   * @memberof WS
   * @member LOADING_ERROR
   */
  LOADING_ERROR: output.LOADING_ERROR,

  /** Error in caching mechanism
   * @memberof WS
   * @member STORAGE_ERROR
   */
  STORAGE_ERROR: output.STORAGE_ERROR,

  /** Error in engine initialization
   * @memberof WS
   * @member INITIALIZATION_ERROR
   */
  INITIALIZATION_ERROR: output.INITIALIZATION_ERROR,

  /** Developers error. Shouldn't occur normally
   * @memberof WS
   * @member DEVELOPERS_ERROR
   */
  DEVELOPERS_ERROR: output.DEVELOPERS_ERROR,

  /** Base plane "XOY"
   * @memberof WS
   * @member BP_XOY
   */
  BP_XOY: basePlane.XOY,

  /** Base plane "YOZ"
   * @memberof WS
   * @member BP_YOZ
   */
  BP_YOZ: basePlane.YOZ,

  /** Base plane "ZOX"
   * @memberof WS
   * @member BP_ZOX
   */
  BP_ZOX: basePlane.ZOX,

  /** Posteffect color correction
   * @memberof WS
   * @member PE_COLOR_BALANCE
   */
  PE_COLOR_BALANCE: postEffects.COLOR_BALANCE,

  /** Posteffect sharpness
   * @memberof WS
   * @member PE_SHARPNESS
   */
  PE_SHARPNESS: postEffects.SHARPNESS,

  /** Posteffect grain
   * @memberof WS
   * @member PE_GRAIN
   */
  PE_GRAIN: postEffects.GRAIN,

  /** Posteffect vignette
   * @memberof WS
   * @member PE_VIGNETTE
   */
  PE_VIGNETTE: postEffects.VIGNETTE,

  /** Fittig mode expand
   * @memberof WS
   * @member FM_EXPAND
   */
  FM_EXPAND: fittingMode.EXPAND,

  /** Fittig mode exact
   * @memberof WS
   * @member FM_EXACT
   */
  FM_EXACT: fittingMode.EXACT,

  /** Fittig mode robust
   * @memberof WS
   * @member FM_ROBUST
   */
  FM_ROBUST: fittingMode.ROBUST,

  /** CAD primitive plane
   * @memberof WS
   * @member CP_PLANE
   */
  CP_PLANE: cadPrimitive.PLANE,

  /** CAD primitive sphere
   * @memberof WS
   * @member CP_SPHERE
   */
  CP_SPHERE: cadPrimitive.SPHERE,

  /** CAD primitive cone
   * @memberof WS
   * @member CP_CONE
   */
  CP_CONE: cadPrimitive.CONE,

  /** CAD primitive torus
   * @memberof WS
   * @member CP_TORUS
   */
  CP_TORUS: cadPrimitive.TORUS,

  /** CAD primitive cylinder
   * @memberof WS
   * @member CP_CYLINDER
   */
  CP_CYLINDER: cadPrimitive.CYLINDER,

  /** Measurement type linear measure
   * @memberof WS
   * @member MET_LINEAR_MEASURE
   */
  MET_LINEAR_MEASURE: objectType.LINEAR_MEASURE,

  /** Measurement type geodesic measure
   * @memberof WS
   * @member MET_GEODESIC_MEASURE
   */
  MET_GEODESIC_MEASURE: objectType.GEODESIC_MEASURE,

  /** Measurement type section
   * @memberof WS
   * @member MET_SECTION
   */
  MET_SECTION: objectType.SECTION,

  /** Measurement type Distance Map
   * @memberof WS
   * @member MET_DISTANCE_MAP
   */
  MET_DISTANCE_MAP: objectType.DISTANCE_MAP,

  /** Cad Type model
   * @memberof WS
   * @member CAD_MODEL
   */
  CAD_MODEL: cadType.MODEL,

  /** Cad Type cylinder
   * @memberof WS
   * @member CAD_CYLINDER
   */
  CAD_CYLINDER: cadType.CYLINDER,

  /** Cad Type cone
   * @memberof WS
   * @member CAD_CONE
   */
  CAD_CONE: cadType.CONE,

  /** Cad Type truncated cone
   * @memberof WS
   * @member CAD_TRUNCATED_CONE
   */
  CAD_TRUNCATED_CONE: cadType.TRUNCATED_CONE,

  /** Cad Type sphere
   * @memberof WS
   * @member CAD_SPHERE
   */
  CAD_SPHERE: cadType.SPHERE,

  /** Cad Type plane
   * @memberof WS
   * @member CAD_PLANE
   */
  CAD_PLANE: cadType.PLANE,

  /** Cad Type torus
   * @memberof WS
   * @member CAD_TORUS
   */
  CAD_TORUS: cadType.TORUS,

  /** Cad Type freeform
   * @memberof WS
   * @member CAD_FREEFORM
   */
  CAD_FREEFORM: cadType.FREEFORM,

  /** Cad Type undefined
   * @memberof WS
   * @member CAD_UNDEFINED
   */
  CAD_UNDEFINED: cadType.UNDEFINED,

  /** Photo state default
   * @memberof WS
   * @member PS_DEFAULT
   */
  PS_DEFAULT: photoState.DEFAULT,

  /** Photo state hovered
   * @memberof WS
   * @member PS_HOVERED
   */
  PS_HOVERED: photoState.HOVERED,

  /** Photo state default
   * @memberof WS
   * @member PS_ACTIVE
   */
  PS_ACTIVE: photoState.ACTIVE,

  /** Scan is displayed by triangulated frames
   * @memberof WS
   * @member SCM_TRIANGLES
   */
  SCM_TRIANGLES: scanMode.TRIANGLES,

  /** Scan is displayed by surfels high resolution
   * @memberof WS
   * @member SCM_SURFELS_HIGH
   */
  SCM_SURFELS_HIGH: scanMode.SURFELS_HIGH,

  /** Scan is displayed by surfels mid resolution
   * @memberof WS
   * @member SCM_SURFELS_MID
   */
  SCM_SURFELS_MID: scanMode.SURFELS_MID,

  /**
   * Enable/Disable Camera controls. This includes rotation and movement
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled Whether controls should be enabled or not
   * @method setControlsEnabled
   */
  setControlsEnabled({enabled}) {
    appState.setStateValue("enableCameraControls", enabled);
    if (enabled) {
      windowManager.enableControls();
    } else {
      windowManager.disableControls();
    }
  },

  /**
   * Set Camera control type. This includes rotation and movement
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(CC_ORBIT|CC_TRACKBALL)} params.type Type of new control
   * @method setControlType
   */
  setControlType({type}) {
    appState.setStateValue("cameraControl", type);
    viewcube.updateRotationArrowsAppearance();

    windowManager.applyToAllWindows((window) => {
      const camera = window.renderGraph.getWorkspaceScene().camera;
      const newControl = controlFactory.createControl(type, camera);
      const target = controlUtils.getViewTargetPlaneIntersection(camera, window.cameraControl.target);
      //hack to avoid target interpolation since we need only angle fix
      vec3.copy(newControl.target, target);

      window.setCameraControl(newControl);
      window.cameraControl.setTarget(target, () => {
          window.renderGraph.requestFrame();
        },
        () => {
          window.bufferReader.dropBuffers();
          window.renderGraph.requestFrame();
        });
    });
  },

  /**
   * Clear selection
   * @memberof WS
   * @method clearSelection
   */
  clearSelection() {
    const objects = workspace.getObjects().filter(object => object.getVisibility() && !object.getLockState());
    commonAPI.clearSelection(objects);
    eraseManipulator.clear();
    events.emit("user_action", {type: msgType.CLEAR_SELECTION});
    events.emit("frame_requested");
  },

  /**
   * Invert selection
   * @memberof WS
   * @method invertSelection
   */
  invertSelection() {
    const objects = workspace.getObjects().filter(object => object.getVisibility() && !object.getLockState());
    commonAPI.invertSelection(objects);
    eraseManipulator.invert();
    events.emit("user_action", {type: msgType.INVERT_SELECTION});
    events.emit("frame_requested");
  },

  /**
   * Enable specific tool
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(TT_VIEW|TT_ALIGN|TT_ERASER|TT_SMOOTHER|TT_TRANSFORM|TT_LOD|TT_POSITIONER|TT_DEFEATURE|TT_MEASUREMENTS|TT_CADFITTER|TT_TEXTURE_HEALER)} params.type Type of the tool
   * @method enableTool
   * @returns Promise() Fulfilled after manipulator completely enabled
   */
  async enableTool(params) {
    let type = params.type;
    const oldType = appState.getStateValue("activeTool");
    const backFaceMode = appState.getStateValue("backFaceMode");

    const xRayEnabled = appState.getStateValue("colorMode") === appState.CM_XRAY;
    const incompatibleBackface = backFaceMode === appState.BM_NONE || backFaceMode === appState.BM_TRANSPARENT;
    const needsDepth = checkNeedsDepth(type);
    if ((xRayEnabled || incompatibleBackface) && needsDepth) {
      output.error(output.API_ERROR, "Scene settings are not compatible with current tool");
      return;
    }

    if (oldType === type) {
      return;
    }

    const activeManipulator = getManipulator(appState.getStateValue("activeTool"));

    if (activeManipulator) {
      activeManipulator.setEnabled(false);
    }

    const newManipulator = getManipulator(type);

    if (!newManipulator) {
      output.error(output.API_ERROR, "Wrong Tool type: \"" + type + "\"");
      return;
    }

    // NOTE: interface does't have ST_NONE type
    if ((type === appState.TT_ERASER || type === appState.TT_CADFITTER || type === appState.TT_DEFEATURE || type === appState.TT_TEXTURE_HEALER)
        && appState.getStateValue("selectionType") === appState.ST_NONE) {
      appState.setStateValue("selectionType", appState.ST_SPHERE);
    }

    newManipulator.setEnabled(true);
    appState.setStateValue("activeTool", type);

    _updateDataStructuresUsage();
    updateGeneralPurposeBufferUsage();
  },

  /**
   * Set the strength of smooth brush
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.strength Smoothing strength
   * @method smoothSetStrength
   */
  smoothSetStrength({strength}) {
    smoothManipulator.setStrength(strength);
    appState.setStateValue("smoothStrength", strength);
  },

  /**
   * Init Web Studio
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {HTMLCanvasElement} params.canvas Canvas used to draw 3D content
   * @param {Number} [params.memoryLimit] Memory limit in bytes for the loaded geometry. If
   * an application exceeds this limit, geometry data starts to unload.
   * @param {Boolean} [params.cacheUnloadedData] Is unloaded geometry cached on client side
   * @param {Boolean} [params.enableManipulatorsWarnings] Report manipulators warnings if true
   * @param {Boolean} [params.viewOnlyMode] View only mode. Geometry is being unloaded after loading
   * @param {Boolean} [params.clientAlign] Align is calculated on the client side (otherwise on the server side)
   * @param {Boolean} [params.adaptiveSurfelsFiltering] Surfels filtering is being switched on camera move
   * @method init
   */
  async init({canvas, memoryLimit, cacheUnloadedData = false, enableManipulatorsWarnings = true,
               viewOnlyMode = false, clientAlign = false, adaptiveSurfelsFiltering = false}) {
    if (!GL.getContext(canvas)) {
      output.error(output.INITIALIZATION_ERROR, "Couldn't initialize webgl context!");
      return;
    }
    config.recalculateInterfaceDepth = extensions.checkDepthRecalc();

    const commonPromise = commonFacadeFunctions.init(canvas);
    objState.init();

    const promises = [commonPromise];

    config.cacheUnloadedData = cacheUnloadedData;
    config.enableManipulatorsWarnings = enableManipulatorsWarnings;
    config.viewOnlyMode = viewOnlyMode;
    config.clientAlign = clientAlign;
    config.adaptiveSurfelsFiltering = adaptiveSurfelsFiltering;

    if (config.cacheUnloadedData) config.cacheUnloadedData = await indexedDataBase.init();

    history.setMaxUndos(config.maxHistoryUndos);
    history.restart();

    if (memoryLimit) {
      config.geometryMemoryLimit = memoryLimit;
    } else {
      config.geometryMemoryLimit = getMaxAvailableMemory();
    }

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const mainRenderGraph = mainWindow.renderGraph;

    mainRenderGraph.setEnabled(renderGraph.GRID, true);
    mainRenderGraph.setEnabled(renderGraph.PHOTOS, true);
    mainRenderGraph.setEnabled(renderGraph.MEASURE_LINES, true);
    mainRenderGraph.setEnabled(renderGraph.POSTEFFECT, true);

    //NOTE: we do not turn off OUTLINE pass
    mainRenderGraph.setEnabled(renderGraph.OUTLINE, true);

    const workspaceScene = mainRenderGraph.getWorkspaceScene();
    const camera = workspaceScene.camera;

    initEvents(canvas);

    promises.push(initZSTDWorkers());
    promises.push(textWidget.init());
    promises.push(cache.init());

    grid.init();
    normals.init();
    hud.init(canvas.parentElement);
    selectionWidgets.init();
    photos.init();

    viewManipulator = new View(camera);
    alignManipulator = new Align();
    eraseManipulator = new Eraser(camera);
    cadFitterManipulator = new CadFitter(camera);
    cadPickerManipulator = new CadPicker(camera);
    smoothManipulator = new Smoother(camera);
    transformManipulator = new TransformManipulator(workspaceScene);
    positionManipulator = new Positioner(camera);
    measurementsManipulator = new Measurements(camera);
    defeatureManipulator = new Defeature(camera);
    textureHealerManipulator = new TextureHealer(camera);

    promises.push(initCadAxis());
    promises.push(initMeasureLines());
    promises.push(gizmo.init());

    promises.push(this.enableTool({type: appState.TT_VIEW}));

    commonFacadeFunctions.startRenderingCycle(canvas, frame, resize);

    await Promise.all(promises);
  },

  /**
   * Set selection type
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {ST_SPHERE|ST_CIRCLE|ST_RECTANGLE|ST_LASSO|ST_CUT_OFF_PLANE|ST_NONE} params.type Selection type
   * @method setSelectionType
   */
  setSelectionType(params) {

    const type = params.type;

    if (appState.getStateValue("selectionType") === type) {
      return false;
    }

    switch (type) {
    case appState.ST_SPHERE:
    case appState.ST_CIRCLE:
    case appState.ST_RECTANGLE:
    case appState.ST_LASSO:
    case appState.ST_CUT_OFF_PLANE:
    case appState.ST_NONE:
      break;
    default:
      output.error(output.API_ERROR, "Can't set selection type. Wrong selection type: \"" + type + "\"");
      return false;
    }

    eraseManipulator.setSelectionType(type);
    cadFitterManipulator.setSelectionType(type);
    defeatureManipulator.setSelectionType(type);
    textureHealerManipulator.setSelectionType(type);
    appState.setStateValue("selectionType", type);

  },

  /**
   * Set selection mode (select/unselect)
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {SM_SELECT|SM_UNSELECT} params.mode Selection mode to be used in selection tool
   * @method setSelectionMode
   */
  setSelectionMode(params) {
    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;

    const selectionPass = mainRenderGraph.getPass(renderGraph.SELECTION);
    const lassoPass = mainRenderGraph.getPass(renderGraph.LASSO);

    const selectionScene = selectionPass.getMainScene();
    const lassoScene = lassoPass.getSecondScene();

    const selectionScreenQuad = selectionPass.getScreenQuad(selectionScene);
    const lassoScreenQuad = lassoPass.getScreenQuad(lassoScene);

    const sc = config.selectionColor;
    const dc = config.deselectionColor;

    switch (params.mode) {
    case appState.SM_SELECT:
      selectionScreenQuad.setDiffuseColor(sc.r, sc.g, sc.b);
      lassoScreenQuad.setDiffuseColor(sc.r, sc.g, sc.b);
      break;
    case appState.SM_UNSELECT:
      selectionScreenQuad.setDiffuseColor(dc.r, dc.g, dc.b);
      lassoScreenQuad.setDiffuseColor(dc.r, dc.g, dc.b);
      break;
    default:
      output.error(output.API_ERROR, "Wrong selection mode: \"" + params.mode + "\"");
      return;
    }

    appState.setStateValue("selectionMode", params.mode);

  },

  /**
   * Set the radius of selection sphere
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.radius Radius of the sphere
   * @method setSelectionSphereRadius
   */
  setSelectionSphereRadius(params) {

    const radius = params.radius;

    if (appState.getStateValue("selectionSphereRadius") === radius) {
      return false;
    }

    appState.setStateValue("selectionSphereRadius", radius);

    selectionWidgets.updateSphereRadius();

  },

  /**
   * Set the radius of selection circle
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.radius Radius of the circle in pixels
   * @method setSelectionCircleRadius
   */
  setSelectionCircleRadius(params) {

    const workspaceScene = workspace.getScene();
    const radius = params.radius;

    if (appState.getStateValue("selectionCircleRadius") === radius) {
      return;
    }
    appState.setStateValue("selectionCircleRadius", radius);

    if (!workspaceScene) {
      return;
    }

    const camera = workspaceScene.camera;
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const globalViewport = mainWindow.getViewport();
    const {x, y} = screenUtils.toScreen(globalViewport, camera.viewport,
      globalViewport[0] + camera.viewport[0] + camera.viewport[2] / 2,
      globalViewport[1] + camera.viewport[1] + camera.viewport[3] / 2);

    const selectionType = appState.getStateValue("selectionType");

    switch (selectionType) {
      case appState.ST_CIRCLE:
        selectionWidgets.setSelectionTypeEnabled(selectionType, true);
    }

    selectionWidgets.updateCircleRadius();
    selectionWidgets.setCircleLocation(x, y);

  },

  /**
   * Set the length of normals
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.length Length of normals
   * @method setNormalsLength
   */
  setNormalsLength(params) {
    const virtualLength = params.length;

    if (appState.getStateValue("normalsLength") === virtualLength) {
      return;
    }
    appState.setStateValue("normalsLength", virtualLength);
    normals.updateLength();
  },

  /**
   * Set the size multiplier of surfels noise
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.size Size multiplier for noise [0; 5]
   * @method setSurfelsNoiseSize
   */
  setSurfelsNoiseSize(params) {
    appState.setStateValue("surfelsNoiseSize", params.size);
    synchronizeObjectsState();
  },

  /**
   * Set whether selection should process even occluded triangles (to select through the whole object)
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled If true, will select through the object
   * @method setSelectThrough
   */
  setSelectThrough() {
    output.error(output.API_ERROR, "selectThrough is not implemented yet");
    //appState.setStateValue("selectThrough", params.enabled);
  },

  /**
   * Set camera type
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {CT_ORTHO|CT_PERSPECTIVE} params.type Type of the camera
   * @method setCameraType
   */
  setCameraType({type}) {

    switch (type) {
    case appState.CT_ORTHO:
    case appState.CT_PERSPECTIVE:
      break;
    default:
      output.error(output.API_ERROR, "Wrong camera type: \"" + type + "\"");
      return;
    }

    appState.setStateValue("cameraType", type);
    const animationTime = 0.2;
    windowManager.applyToAllWindows((currWindow) => {
      const windowRenderGraph = currWindow.renderGraph;
      const gridPass = windowRenderGraph.getPass(renderGraph.GRID);
      const gridScene = gridPass.getSecondScene();
      const gridCamera = gridScene.camera;
      const cameraControl = currWindow.cameraControl;

      cameraControl.setProjectionType(type, gridCamera, () => {
        scheduleDrawQueries(false);
      },
      () => {
        windowRenderGraph.requestFrame();
      },
      () => {
        currWindow.bufferReader.dropBuffers();
        events.emit("visibility_changed");
        windowRenderGraph.requestFrame();
      },
      animationTime);
    });
  },

  /**
   * Set whether annotation mode are enabled or not
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled Whether to enable annotation mode or not
   * @method setAnnotationModeEnabled
   */
  setAnnotationModeEnabled({enabled}) {
    const backFaceMode = appState.getStateValue("backFaceMode");

    const incompatibleBackface = backFaceMode === appState.BM_NONE || backFaceMode === appState.BM_TRANSPARENT;
    const xRayEnabled = appState.getStateValue("colorMode") === appState.CM_XRAY;
    if (xRayEnabled || incompatibleBackface) {
      output.error(output.API_ERROR, "Scene settings are not compatible with annotations mode");
      return;
    }

    if (appState.getStateValue("annotationsEnabled") === enabled) {
      return;
    }

    appState.setStateValue("annotationsEnabled", enabled);
    updateGeneralPurposeBufferUsage();
    if (!enabled) {
      annotationsModule.resetAnnotations(null);
    }
    annotationsModule.updateAnnotations();
  },

  /**
   * Checks annotation appearance in 3d scene
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.clientX Screen coordinate X
   * @param {Number} params.clientY Screen coordinate Y
   * @method checkAnnotationPoint
   * @returns Promise({Object}) meta Information about annotation appearance
   * @param {String} meta.parentID ID of the object to which the annotation should be attached or null
   * @param {Array(3)} meta.localPoint annotation position in object/scene local space
   * @param {Array(4)} meta.cameraLocalQuat camera rotation at this moment
   * @param {Array(3)} meta.cameraLocalPos camera position at this moment
   */
  checkAnnotationPoint({clientX, clientY}) {
    return annotationsModule.createAnnotationMeta(clientX, clientY);
  },

  /**
   * Show annotation points
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Object} params.annotations List of annotations' metadata
   * @param {String} params.annotations[i].uuid annotation UUID
   * @param {String} params.annotations[i].parentID ID of the object to which the annotation should be attached or null
   * @param {Array(3)} params.annotations[i].localPoint annotation position in object/scene local space
   * @param {Array(4)} params.annotations[i].cameraLocalQuat camera rotation at the annotation creation moment
   * @param {Array(3)} params.annotations[i].cameraLocalPos camera position at the annotation creation moment
   * @method showAnnotationPoints
   */
  showAnnotationPoints({annotations}) {
    annotationsModule.resetAnnotations(annotations);
    if (appState.getStateValue("annotationsEnabled")) {
      annotationsModule.updateAnnotations();
    }
  },

  /**
   * Move camera to the annotation point
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.uuid uuid of the annotation
   * @method moveToAnnotationPoint
   */
  moveToAnnotationPoint({uuid}) {
    const annotation = annotationsModule.getAnnotationByUUID(uuid)
    if (!annotation) {
      output.error(output.API_ERROR, "Can't move camera to the annotation point. Wrong annotation uuid: \"" + uuid + "\"");
      return;
    }

    const camera = workspace.getScene().camera;
    animateCameraToAnnotation(camera, annotation);
  },

  /**
   * Set whether Objects' normals are visible or not
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.isVisible Are objects' normals visible
   * @method setNormalsVisibility
   */
  setNormalsVisibility({isVisible}) {
    if (!extensions.get("ANGLE_instanced_arrays")) {
      output.error(output.INITIALIZATION_ERROR, "Can't show normals. Instanced draw is unsupported");
      return;
    }

    appState.setStateValue("showNormals", isVisible);
    appState.setState(appState.GS_NORMALS, isVisible);

    normals.setEnabled(isVisible);

    synchronizeObjectsState();
  },

  /**
   * Set whether Objects' bboxes are visible or not
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.isVisible Are objects' bboxes visible
   * @method setBboxVisibility
   */
  setBboxVisibility ({isVisible}) {
    setBboxVisibility(isVisible);
  },

  /**
   * Set whether world grid is visible or not
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.isVisible Is grid visible
   * @method setGridVisibility
   */
  setGridVisibility({isVisible}) {

    if (appState.getStateValue("showGrid") == isVisible) {
      return;
    }

    appState.setStateValue("showGrid", isVisible);

    if (isVisible) {
      grid.showGrid();
    } else {
      grid.hideGrid();
    }

    events.emit("frame_requested");

  },

  /**
   * Set the color of grid
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {{ r, g, b }} params.color Object color. Should be object with values of red, green and blue. Each component should be in range [0..1]
   * @method setGridColor
   */
  setGridColor({color}) {
    grid.setGridColor(color.r, color.g, color.b);
  },

  /**
   * Set the background texture
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.path Path to the background texture file
   * @method setBackgroundTexture
   */
  async setBackgroundTexture({path}) {
    let backgroundTexture = null;
    if (path) {
      try {
        const image = await loader.load(path, loader.IMAGE);

        if (!image) {
          output.error(output.DEVELOPERS_ERROR, "Couldn't load background texture.");
          return;
        }

        const minFilter = textureMinFilter.NEAREST;
        const magFilter = textureMagFilter.NEAREST;

        backgroundTexture = bufferFactory.create(pixelBufferType.T_RGBA, {
          width: image.width, height: image.height,
          minFilter, magFilter, immutable: true, flipY: true
        });
        backgroundTexture.initMipMapLevelImage(0, image);
      } catch (error) {
        if (!loader.isLegalError(error)) {
          output.error(output.DEVELOPERS_ERROR, "Couldn't load background texture. " + error);
          return;
        }
      }
    }
    config.backgroundTexture = backgroundTexture;

    windowManager.applyToAllWindows((window) => {
      window.setBackgroundTexture(backgroundTexture);
      window.renderGraph.requestFrame();
    });
  },

  /**
   * Load project by its directory name
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {string} params.path Path to the project sjson
   * @method loadProject
   * @returns {Promise} Promise resolved when the project structure has finished loading
   */
  async loadProject(params) {
    measurements.cleanup();
    const json = await commonFacadeFunctions.loadProject(params);
    if (!json) {
      return;
    }
    photos.reset();
    _highlightedObject = null;

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const areAnyObjectsVisible = Boolean(workspace.getObjects().find(object => object.getVisibility()));
    const fitToViewPredicate = areAnyObjectsVisible ? (object => object.getVisibility()) : null;

    transformManipulator.update();
    const workspaceScene = workspace.getScene();
    gizmo.updateGizmo(workspaceScene.camera);

    const setCameraPosition = () => {
      mainWindow.cameraControl.setDefaultPosition(workspace.getBBox(fitToViewPredicate));
      events.off("objects_initialized", setCameraPosition);
    };
    events.on("objects_initialized", setCameraPosition);

    const startAnimationHandler = () => {
      grid.smoothlyShow();
      events.off("loader_has_been_warmed_up", startAnimationHandler);
    };
    events.on("loader_has_been_warmed_up", startAnimationHandler);

    selectionWidgets.updateCircleRadius();

    _updateDataStructuresUsage();
    await synchronizeObjectsState();

    setBboxVisibility(appState.getStateValue("bboxVisibility"));

    await workspace.initObjects();
    monitoring.setHighSpeedReportEnabled(true);
    await workspace.startProjectLoading();
    monitoring.setHighSpeedReportEnabled(false);
    photos.update();
    await measurements.update();
    updateGeneralPurposeBufferUsage();
  },

  /**
   * Set current project url (explicit)
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {string} params.path Path to the project sjson
   * @method setUrlExplicit
   */
  setUrlExplicit(params) {
    reformer.setProjectPath(params.path);
  },

  /**
   * Duplicate existing scan of fusion
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array} params.objectIDs Scans, Fusions or Groups ids list
   * @method duplicateObjects
   */
  duplicateObjects({objectIDs}) {

    if (!objectIDs) {
      output.error(output.API_ERROR, "Can't duplicate objects. Please provide the \"objectIDs\" field.");
      return;
    }

    const payload = workspace.duplicateObjects(objectIDs);
    events.emit("user_action", {type: msgType.DUPLICATE_OBJECT, payload});

  },

  /**
   * Destroy the application
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {HTMLCanvasElement} params.canvas Canvas used to draw 3D content
   * @method destroy
   */
  destroy({canvas}) {
    photos.cleanup();
    measurements.cleanup();
    viewManipulator.cleanup();
    alignManipulator.cleanup();
    eraseManipulator.cleanup();
    cadFitterManipulator.cleanup();
    smoothManipulator.cleanup();
    transformManipulator.cleanup();
    positionManipulator.cleanup();
    measurementsManipulator.cleanup();
    defeatureManipulator.cleanup();
    textureHealerManipulator.cleanup();
    cadPickerManipulator.cleanup();

    viewManipulator = null;
    alignManipulator = null;
    eraseManipulator = null;
    cadFitterManipulator = null;
    smoothManipulator = null;
    transformManipulator = null;
    positionManipulator = null;
    measurementsManipulator = null;
    defeatureManipulator = null;
    textureHealerManipulator = null;
    cadPickerManipulator = null;

    if (config.cacheUnloadedData) indexedDataBase.cleanup();

    grid.cleanup();
    selectionWidgets.cleanup();
    textWidget.cleanup();
    mirror.cleanup();
    bbox.cleanup();
    normals.cleanup();
    hud.cleanup();
    cleanupRotationIndicator();
    clearMeasureLines();
    clearCadAxis();
    loadingProgress.cleanup();

    _highlightedObject = null;
    _selectedGeometries.clear();
    _objectPreviewTasks.length = 0;

    commonFacadeFunctions.destroy(canvas);
  },

  /**
   * Set Shading Type for all scans and fusions
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {SHT_SMOOTH|SHT_FLAT} params.type Shading type
   * @method setShading
   */
  setShading({type}) {

    switch (type) {
    case appState.SHT_SMOOTH:
    case appState.SHT_FLAT:
      break;
    default:
      output.error(output.API_ERROR, "Wrong Shading type: \"" + type + "\"");
      return;
    }

    appState.setState(appState.GS_NORMALS, type === appState.SHT_SMOOTH);
    appState.setStateValue("shadingType", type);

    synchronizeObjectsState();

  },

  /**
   * Set highlight of an object by ID
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Scan or Fusion id
   * @method setObjectHighlight
   */
  setObjectHighlight({objectID}) {
    const outlinePass = windowManager.getWindow(windowManager.MAIN).renderGraph.getPass(renderGraph.OUTLINE);

    const object = workspace.getObjectByID(objectID);
    if (_highlightedObject === object) {
      return;
    }

    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
    if (_highlightedObject) {
      workspace.deactivateObjectOutline(_highlightedObject);
    }

    hideNonSelectedObjects(object);
    windowManager.applyToAllWindows(window => window.updateObjectsScene());

    if (!object) {
      _highlightedObject = null;
      return;
    }

    workspace.activateObjectOutline(object, outlinePass, OBJECT_HIGHLIGHT);

    _highlightedObject = object;

  },

  /**
   * Set transparency of an object by name
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Scan or Fusion id
   * @param {Number} params.transparency requested object's transparency in range [0; 1]
   * @method objectSetTransparency
   */
  objectSetTransparency({objectID, transparency}) {

    const object = workspace.getObjectByID(objectID);

    if (!object) {
      output.error(output.API_ERROR, "Can't set object transparency. Wrong object id: \"" + objectID + "\"");
      return false;
    }

    object.setOpacity(transparency);

    synchronizeObjectsState();
    windowManager.applyToAllWindows(window => window.updateObjectsScene());

    object.model.shader.dropID();

    events.emit("frame_requested");

  },

  /**
   * Save all unsaved info. This method forces several "save" events to be emited
   * @memberof WS
   * @method save
   */
  save() {
    workspace.saveJSON();
  },

  /**
   * Emits "user_action" event of the SAVE type
   * @memberof WS
   * @method saveNotify
   */
  saveNotify() {
    events.emit("user_action", {type: msgType.SAVE});
  },

  /**
   * Fit Camera View to visible objects or to given object
   * @memberof WS
   * @param {Number} params.minx X coord of min point
   * @param {Number} params.miny Y coord of min point
   * @param {Number} params.maxx X coord of max point
   * @param {Number} params.maxy Y coord of max point
   * @param {Array} [params.objectIDs] List of objects' ids
   * @method fitCamera
   */
  fitCamera({minx, miny, maxx, maxy, objectIDs = []}) {

    if (maxx - minx <= 0 || maxy - miny <= 0) {
      output.error(output.API_ERROR, "Can't fit view to objects. Wrong screen rectangle size.");
      return;
    }

    if (animation.checkBusy()) {
      return;
    }

    let minmax;
    if (objectIDs.length) {
      const pred = object => objectIDs.includes(object.getID());
      minmax = workspace.getBBox(pred);
    } else {
      minmax = windowManager.getWindow(windowManager.MAIN).getMinMax();
    }

    if (!minmaxUtils.isValid(minmax)) {
      return;
    }

    const mainWindow = windowManager.getWindow(windowManager.MAIN);

    const workspaceScene = workspace.getScene();
    const camera = workspaceScene.camera;

    const minNDC = screenUtils.clientToScreen(mainWindow.getViewport(), camera.viewport, minx, miny);
    const maxNDC = screenUtils.clientToScreen(mainWindow.getViewport(), camera.viewport, maxx, maxy);

    const rect = {
      minx : Math.min(minNDC.x, maxNDC.x),
      miny : Math.min(minNDC.y, maxNDC.y),
      maxx : Math.max(minNDC.x, maxNDC.x),
      maxy : Math.max(minNDC.y, maxNDC.y),
    };

    animateCamera(camera, rect, minmax);

  },

  /**
   * Set application rendering mode
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(RM_SOLID|RM_WIREFRAME|RM_POINTS)} params.mode New rendering mode
   * @method setRenderMode
   */
  setRenderMode({mode}) {
    appState.setStateValue("renderMode", mode);

    synchronizeApplicationState();
    synchronizeObjectsState().then(() => {
      windowManager.applyToAllWindows(window => window.bufferReader.dropBuffers());
      events.emit("visibility_changed");
    });
  },

  /**
   * Set application color mode
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(CM_TEXTURE|CM_SCAN_COLOR|CM_FRAME_COLOR|CM_MAX_ERROR|CM_XRAY)} params.mode New color mode
   * @method setColorMode
   */
  setColorMode({mode}) {
    const xRayEnabled = mode === appState.CM_XRAY;
    const toolType = appState.getStateValue("activeTool");
    const needsDepth = checkNeedsDepth(toolType);
    if (xRayEnabled && needsDepth) {
      output.error(output.API_ERROR, "X-ray color mode is not compatible with current tool type or annotations mode");
      return;
    }

    appState.setStateValue("colorMode", mode);
    synchronizeApplicationState();
    synchronizeObjectsState().then(() => {
      windowManager.applyToAllWindows(window => {
        window.bufferReader.dropBuffers();
        window.updateObjectsScene();
      });
      events.emit("visibility_changed");
    });
  },

  /**
   * Set surfels filtering mode
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(SM_NONE|SM_CONCURRENT|SM_BLEND)} params.mode New surfels filtering mode
   * @method setSurfelsFilteringMode
   */
  setSurfelsFilteringMode({mode}) {
    appState.setStateValue("surfelsFilteringMode", mode);
    synchronizeObjectsState();

    const windowFunction = window => {
      window.bufferReader.dropBuffers();
      window.updateObjectsScene();
    };

    windowManager.applyToAllWindows(windowFunction);
    events.emit("visibility_changed");
  },

  /**
   * Make all frames visible (or make visible only keyframes/textured frames)
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled Whether to make all frames visible or not
   * @method setAllFramesEnabled
   */
  setAllFramesEnabled({enabled}) {
    if (appState.getStateValue("showAllFrames") === enabled) {
      return;
    }

    appState.setStateValue("showAllFrames", enabled);
    const objects = workspace.getObjects();
    const promises = [];
    for (let object of objects) {
      if (object.getVisibility()) {
        promises.push(workspace.updateNonTexturedFrames(object));
      }
    }
    if (enabled) {
      workspace.setObjectState(objState.OS_NORMAL, false);
      workspace.setObjectState(objState.OS_OCTREE, false);
      workspace.setObjectState(objState.OS_HALF_EDGE, false);
    }

    Promise.all(promises).then(() => {
      synchronizeObjectsState();
    });
  },

  /**
   * Set X-Ray parameters
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} [params.edgeFalloff] Determines the strength of falloff on the edges of the model. Should be in range [0.01..0.3]
   * @param {Number} [params.intensity] Intensity of X-Ray effect. Should be in range [0.01..2.0]
   * @method xraySetParams
   */
  xraySetParams(params) {

    if ("edgeFalloff" in params) {
      appState.setStateValue("xrayEdgeFalloff", params.edgeFalloff);
    } else if (!("intensity" in params)) {
      return;
    }

    if ("intensity" in params) {
      appState.setStateValue("xrayIntensity", params.intensity);
    }

    const objects = workspace.getObjects();

    for (let i = 0; i < objects.length; ++i) {

      const shader = objects[i].model.shader;

      if ("edgeFalloff" in params) {
        shader.setXrayEdgeFalloff(params.edgeFalloff);
      }

      if ("intensity" in params) {
        shader.setXrayIntensity(params.intensity);
      }

    }

  },

  /**
   * Set color of a given object
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.id Object or Group id
   * @param {{ r, g, b }} params.color Object color. Should be object with values of red, green and blue. Each component should be in range [0..1]
   * @method setObjectColor
   */
  setObjectColor(params) {
    if (commonAPI.setObjectColor(params)) {
      const object = workspace.getObjectByID(params.id);
      if (object) {
        if (object.type !== objectType.SURFELS) {
          object.updateFramesTemporaryTextures();
        }
      }
      events.emit("frame_requested");
      events.emit("user_action", {type: msgType.SET_OBJECT_COLOR, payload: params});
    }
  },

  /**
   * Set material of a given object
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Object id
   * @param {Object} params.settings Material settings object. Optional
   * @param {(OM_ASTUDIO|OM_MATCAP)} params.material New material type
   * @method setObjectMaterial
   */
  async setObjectMaterial(params) {
    const object = workspace.getObjectByID(params.objectID);

    if (!object) {
      output.error(output.API_ERROR, "Can't set object material. Wrong object id: \"" + params.objectID + "\"");
      return false;
    }

    if (object.type === objectType.SURFELS) {
      output.warn("This object has surfels type. Material changes are not supported");
      return true;
    }

    const promises = [];
    const settings = params.settings ? params.settings : {};
    promises.push(materialFactory.resetMaterial(params.material, object.model, settings));
    for (const childModel of object.model.getChildren()) {
      promises.push(materialFactory.resetMaterial(params.material, childModel, settings));
    }

    await Promise.all(promises);
    synchronizeObjectsState();

    events.emit("frame_requested");
  },

  /**
   * Enable/disable mix object color with constant color
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled Whether color mix should be enabled or disabled
   * @param {String} params.objectID Object id
   * @method setMixColorEnabled
   */
  setMixColorEnabled(params) {

    const object = workspace.getObjectByID(params.objectID);

    if (!object) {
      output.error(output.API_ERROR, "Can't enable color mix. Wrong object id: \"" + params.objectID + "\"");
      return false;
    }

    const shader = object.model.shader;

    if (shader.getDirectiveValue("COLOR_MIX") != params.enabled) {
      object.model.shader.setDirective("COLOR_MIX", params.enabled);
      events.emit("frame_requested");
    }

  },

  /**
   * Set constant color to mix with object's color
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {{ r, g, b }} params.color Constant color. Should be object with values of red, green and blue. Each component should be in range [0..1]
   * @param {String} params.objectID Object id
   * @method setMixColorEnabled
   */
  setMixColor(params) {

    const object = workspace.getObjectByID(params.objectID);

    if (!object) {
      output.error(output.API_ERROR, "Can't set color mix. Wrong object id: \"" + params.objectID + "\"");
      return false;
    }

    const {r, g, b} = params.color;

    object.model.shader.setMixColor(r, g, b);

  },

  /**
   * Set the mix factor to mix the constant color with object's color
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.factor Mix factor. Should be should be in range [0..1]
   * @param {String} params.objectID Object id
   * @method setMixColorEnabled
   */
  setMixColorFactor(params) {

    const object = workspace.getObjectByID(params.objectID);

    if (!object) {
      output.error(output.API_ERROR, "Can't set mix factor. Wrong object id: \"" + params.objectID + "\"");
      return false;
    }

    object.model.shader.setColorMixFactor(params.factor);

  },

  /**
   * Set name of a given object
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.id Object or Group id
   * @param {String} params.name Name to be assigned
   * @method setObjectName
   */
  setObjectName(params) {
    const object = workspace.getGroupByID(params.id) || workspace.getObjectByID(params.id) || workspace.getPhotoGroupByID(params.id);
    if (!object) {
      output.error(output.API_ERROR, "Can't set object name. Wrong object id: \"" + params.id + "\"");
      return;
    }

    object.setName(params.name);
    events.emit("user_action", {type: msgType.SET_OBJECT_NAME, payload: params});
  },

  /**
   * Show/Hide coordiante axes as a part of grid plane
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.show Whether grid axes should be shown or not
   * @method showGridAxes
   */
  showGridAxes({show}) {

    appState.setStateValue("showGridAxes", show);

    if (show) {
      grid.showAxes();
    } else {
      grid.hideAxes();
    }

  },

  /**
   * Make object preview picture with given dimensions
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Scan or Fusion id
   * @param {Number} params.width Width of the image to be drawn
   * @param {Number} params.height Heigth of the image to be drawn
   * @param {Boolean} [params.useActiveCamera] Use the position of active camera. If false or not provided, the default camera position is used
   * @returns {Promise} Promise resolved when the pixels are read
   * @method makeObjectPreview
   */
  makeObjectPreview({objectID, width, height, useActiveCamera=false}) {

    const object = workspace.getObjectByID(objectID);

    if (!object) {
      output.error(output.API_ERROR, "Can't make object preview. Wrong object id: \"" + objectID + "\"");
      return Promise.resolve();
    }

    return workspace.loadObjectData(object).then(() => {
      return new Promise((resolve) => {
        workspace.updateNonTexturedFrames(object);
        pushPreviewTask(object, width, height, useActiveCamera, resolve);
        popPreviewTask();
      });
    });

  },

  /**
   * Notify WS about project's change
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {string} params.path Path to the directory containing a project
   * @method updateProject
   * @returns {Promise} Promise resolved when the project structure has finished updating
   */
  async updateProject(params) {
    workspace.setObjectState(objState.OS_NORMAL, false);
    workspace.setObjectState(objState.OS_OCTREE, false);
    workspace.setObjectState(objState.OS_HALF_EDGE, false);

    //Note: this call clears graphic normal object
    normals.clear();

    const updatedObjects = await workspace.updateProject(params.path);
    _updateDataStructuresUsage();
    if (updatedObjects !== null) {
      await workspace.initObjects();

      await workspace.startProjectLoading();
      await workspace.reloadObjects();
      events.emit("objects_updated", updatedObjects);

      await synchronizeObjectsState();
      events.emit("user_action", {type: msgType.UPDATE_PROJECT, payload: params});
    }
    photos.update();
    await measurements.update();
    updateGeneralPurposeBufferUsage();
  },

  /**
   * Set backface mode
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(BM_NONE|BM_DIFFUSE|BM_TRANSPARENT|BM_FRONTFACE)} params.mode Current backFace mode
   * @method setBackFaceMode
   */
  setBackFaceMode({mode}) {
    if (appState.getStateValue("backFaceMode") === mode) {
      return;
    }

    const incompatibleBackface = mode === appState.BM_NONE || mode === appState.BM_TRANSPARENT;
    const toolType = appState.getStateValue("activeTool");
    const needsDepth = checkNeedsDepth(toolType);
    if (incompatibleBackface && needsDepth) {
      output.error(output.API_ERROR, "Backface mode is not compatible with current tool type or annotations mode");
      return;
    }

    appState.setStateValue("backFaceMode", mode);

    synchronizeObjectsState();
    synchronizeApplicationState();
    windowManager.applyToAllWindows(window => window.bufferReader.dropBuffers());
    events.emit("visibility_changed");
  },

  /**
   * Set Back Face Color of the objects
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {{ r, g, b }} params.color Back face color. Should be object with values of red, green and blue. Each component should be in range [0..1]
   * @method setBackFaceColor
   */
  setBackFaceColor({ color }) {

    const objects = workspace.getObjects();

    appState.setStateValue("backFaceColor", {"r": color.r, "g": color.g, "b": color.b});

    for (let i = 0; i < objects.length; ++i) {
      objects[i].model.shader.setBackFaceColor(color.r, color.g, color.b);
    }

  },

  /**
   * Set Wireframe Color
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {{ r, g, b }} params.color Wireframe color. Should be object with values of red, green and blue. Each component should be in range [0..1]
   * @method setWireframeColor
   */
  setWireframeColor({ color }) {

    const currentColor = appState.getStateValue("wireframeColor")

    if ( currentColor.r === color.r && currentColor.g === color.g && currentColor.b === color.b ) {
      return;
    }

    wireframeSetColor(color);

  },

  /**
   * Set visibility of an object or component
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Object id
   * @param {Number} params.l1IDx level 1 component index
   * @param {Number} params.l2IDx level 2 component index
   * @param {Boolean} [params.visible] Is object visible.
   * @method setObjectVisibility
   */
  async setObjectVisibility(params) {
    await commonAPI.setObjectVisibility(params);
    if (!params.visible && workspace.getObjectByID(params.objectID) === _highlightedObject) {
      WS.setObjectHighlight({objectID: null});
    }
    _updateDataStructuresUsage();
    synchronizeApplicationState();
    await synchronizeObjectsState();
    photos.update();
    await measurements.update();
    updateGeneralPurposeBufferUsage();
    events.emit("user_action", {type: msgType.SET_OBJECT_VISIBILITY, payload: params});
  },

  /**
   * Set lock state of an object by name
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Scan or Fusion id
   * @param {Boolean} [params.locked] Is object locked
   * @method setObjectLockState
   */
  setObjectLockState(params) {
    const object = workspace.getObjectByID(params.objectID);
    if (!object) {
      output.error(output.API_ERROR, "Can't set object lock state. Wrong object id: \"" + params.objectID + "\"");
      return;
    }
    object.setLockState(params.locked);

    preview.updatePreview(workspace.getObjectByID(params.objectID));
    if (appState.getStateValue("activeTool") === appState.TT_TRANSFORM) {
      transformManipulator.update();
    }
  },

  /**
   * Create or delete mirror of the object
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array} params.point Mirror point coordinates [x, y, z]
   * @param {(BP_XOY|BP_YOZ|BP_ZOX)} [params.plane] Mirror plane
   * @param {Boolean} params.visible Create a mirror or delete it.
   * @method mirrorObjectPreviewVisibility
   */
  async mirrorObjectsPreviewVisibility({ point, plane, visible }) {
    _mirroringState.enabled = visible;
    _mirroringState.point = point;
    _mirroringState.plane = plane;

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const workspaceBlendingScene = mainRenderGraph.getPostBlendingInterfaceScene();

    mirror.preview(workspace.getObjects(), point, plane, workspaceBlendingScene, visible);
    mainRenderGraph.requestFrame();

  },

  /**
   * Apply mirroring to object
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array} params.point Mirror point coordinates [x, y, z]
   * @param {(BP_XOY|BP_YOZ|BP_ZOX)} params.plane Mirror plane
   * @method mirrorObjectApply
   */
  mirrorObjectsApply({ point, plane }) {
    const originalObjects = workspace.getObjects().filter((object) => object.getVisibility() && !object.getLockState());
    return mirror.apply(originalObjects, point, plane);
  },

  /**
   * Apply positioning to scene objects
   * @memberof WS
   * @method positioningApply
   */
  positioningApply() {
    positionManipulator.apply();
  },

  /**
   * Enable or disable positioner camera mode
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled whether to enable or disable positioner camera mode
   * @method positioningSetCameraModeEnabled
   */
  positioningSetCameraModeEnabled(params) {
    positionManipulator.setCameraModeEnabled(params.enabled);
  },

  /**
   * Set positioning plane
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(BP_XOY|BP_YOZ|BP_ZOX)} params.plane Positioning plane
   * @method positioningSetPlane
   */
  positioningSetPlane(params) {
    positionManipulator.setPlane(params.plane);
  },

  /**
   * Invert positioning plane normal
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.inverted whether to invert plane normal
   * @method positioningInvertPlane
   */
  positioningInvertPlane(params) {
    positionManipulator.invertPlaneNormal(params.inverted);
  },

  /**
   * Clear set of positioning points
   * @memberof WS
   * @method positionerReset
   */
  positionerReset() {
    positionManipulator.reset();
  },

  /**
   * Start align calculation after setting align points
   * @memberof WS
   * @method alignCalculate
   * @param {Object} params Method parameters
   * @param {{ minx, miny, maxx, maxy }} params.rect Object screen rect. Should be object with values of min x,y max x,y. Each component should be in client screen coords. Used only in client align
   * @returns Promise() Fulfilled after align calculation in case of client align, and instantly in case of server align
   */
  async alignCalculate({rect = null}) {
    if (config.clientAlign) {
      const rectNDC = getRectNDC(rect);
      await alignManipulator.directAlign(rectNDC);
    } else {
      alignManipulator.sendAlignData();
    }
  },

  /**
   * Set align matrix received from align utility
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array(16)} params.matrix Align matrix
   * @param {{ minx, miny, maxx, maxy }} params.rect Object screen rect. Should be object with values of min x,y max x,y. Each component should be in client screen coords.
   * @method alignSetMatrix
   */
  alignSetMatrix({matrix, rect = null}) {
    const rectNDC = getRectNDC(rect);
    alignManipulator.align(matrix, rectNDC);
  },

  /**
   * Clear set of align points
   * @memberof WS
   * @method alignReset
   */
  alignReset() {
    alignManipulator.reset();
  },

  /**
   * Remove pair of align points
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.index Index of deleted pair
   * @method alignRemovePair
   */
  alignRemovePair({index}) {
    alignManipulator.removePointsPair(index);
  },

  /**
   * Resize pair of align points
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.index Index of resized pair
   * @param {Number} params.diameter New diameter of the points in pixels
   * @method alignResizePair
   */
  alignResizePair({index, diameter}) {
    alignManipulator.resizePointsPair(index, diameter);
  },

  /**
   * Remove align point
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.index Index of deleted pair
   * @param {Booelan} params.isAligner Does point belong to aligner window
   * @method alignRemovePoint
   */
  alignRemovePoint({index, isAligner}) {
    alignManipulator.removePoint(index, isAligner);
  },

  /**
   * Add new window for rendering
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array(4)} params.viewport Window's position on the screen [left, bottom, width, height]
   * @method addWindow
   */
  addWindow({viewport}) {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const camera = mainWindow.renderGraph.getWorkspaceScene().camera;
    windowManager.createWindow(viewport, camera.type);
  },

  /**
   * Delete specified window
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.windowID Window's id
   * @method removeWindow
   */
  removeWindow({windowID}) {
    windowManager.deleteWindow(windowID);
  },

  /**
   * Change window's screen appearance
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.windowID Window's id
   * @param {Array(4)} params.viewport Window's position on the screen [left, bottom, width, height]
   * @method setWindowViewport
   */
  setWindowViewport({windowID, viewport}) {
    const phyViewport = screenUtils.viewportToPhisycalSize(viewport, []);
    windowManager.updateWindowViewport(windowID, phyViewport);
    events.emit("visibility_changed");
    events.emit("canvas_resize");
  },

  /**
   * Assign object to window, required by align
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.windowID Window's id
   * @param {Array} params.objectIDs List of objects' ids
   * @method setWindowObjects
   */
  setWindowObjects({windowID, objectIDs}) {
    const objects = [];

    for (let objectID of objectIDs) {
      const object = workspace.getObjectByID(objectID);
      if (!object) {
        output.error(output.API_ERROR, "Can't add object to window. Wrong object id: \"" + objectID + "\"");
        continue;
      }
      objects.push(object);
    }

    windowManager.setWindowObjects(objects, windowID);
    events.emit("visibility_changed");
    alignManipulator.reset();
    _alignWindowToObjects.set(windowManager.getWindow(windowID), objectIDs);
  },

  /**
   * Group objects
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array} params.objectIDs List of objects' ids to be grouped
   * @param {String} [params.parentGroupId] If provided the new group will be added to the existing group with this ID
   * @param {String} [params.name] Name to be given to the new group
   * @method groupObjects
   */
  groupObjects() {
    //Note: group is normally created on the backend side
    output.error(output.API_ERROR, "groupObjects is not implemented yet");
  },

  /**
   * Add object to the group
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID ID of the object
   * @param {String} params.groupID ID of the group
   * @method addObjectToGroup
   */
  addObjectToGroup(params) {
    const object = workspace.getGroupByID(params.objectID) || workspace.getObjectByID(params.objectID) || workspace.getPhotoGroupByID(params.objectID);
    if (!object) {
      output.error(output.API_ERROR, "Can't set object group. Wrong object id: \"" + params.objectID + "\"");
      return;
    }
    object.setGroup(params.groupID);

    events.emit("user_action", {type: msgType.ADD_OBJECT_TO_GROUP, payload: params});
  },

  /**
   * Remove object from a group
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID ID of the object
   * @method removeObjectFromGroup
   */
  removeObjectFromGroup(params) {
    const object = workspace.getGroupByID(params.objectID) || workspace.getObjectByID(params.objectID) || workspace.getPhotoGroupByID(params.objectID);
    if (!object) {
      output.error(output.API_ERROR, "Can't remove object from group. Wrong object id: \"" + params.objectID + "\"");
      return;
    }
    object.setGroup("");

    events.emit("user_action", {type: msgType.REMOVE_OBJECT_FROM_GROUP, payload: params});
  },

  /**
   * Remove a group
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.groupID ID of the group
   * @method removeGroup
   */
  removeGroup(params) {
    const payload = workspace.removeGroup(params.groupID);
    events.emit("user_action", {type: msgType.REMOVE_GROUP, payload: payload});
  },

  /**
   * Set or unset group color for nested objects
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.groupID ID of the group
   * @param {Boolean} params.enabled Whether to set or unset group color
   * @method groupColorsSetEnabled
   */
  groupColorsSetEnabled(params) {

    const group = workspace.getGroupByID(params.groupID);
    if (group === null) {
      output.error(output.API_ERROR, "Can't set/unset group color. Group with id " + params.groupID + " was not found");
      return;
    }

    workspace.updateColorsForGroupObjects(group, params.enabled);
    events.emit("frame_requested");

  },

  /**
   * Turn lighting on/off
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled Whether to apply lighting or not
   * @method lightingSetEnabled
   */
  lightingSetEnabled({ enabled }) {

    if (appState.getStateValue("lighting") === enabled) {
      return;
    }

    appState.setStateValue("lighting", enabled);

    const objects = workspace.getObjects();

    for (let i = 0; i < objects.length; ++i) {

      const shader = objects[i].model.shader;

      if (shader) {
        shader.setDirective("LIGHTING", enabled);
      }

    }
  },

  /**
   * Erase selected geometry vertices
   * @memberof WS
   * @method eraseSelectedGeometry
   */
  eraseSelectedGeometry() {
    eraseManipulator.erase();
  },

  /**
   * Apply defeature tool to selected geometry
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Fusion id
   * @method defeature
   */
  defeature({objectID}) {
    if (appState.getStateValue("activeTool") !== appState.TT_DEFEATURE) {
      output.error(output.API_ERROR, "Can't defeature. Activate defeaturer manipulator before");
      return;
    }

    const object = workspace.getObjectByID(objectID);
    if (!object || object.type !== objectType.FUSION) {
      output.error(output.API_ERROR, "Can't defeature. Wrong object id: \"" + objectID + "\"");
      return;
    }

    const payload = null; // getFusionSelectionData(object, true);
    payload["objectUuid"] = object.getUUID();

    events.emit("defeature", payload);
  },

  /**
   * Apply textureHealer tool to selected geometry
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Fusion id
   * @method healTexture
   */
  healTexture({objectID}) {
    if (appState.getStateValue("activeTool") !== appState.TT_TEXTURE_HEALER) {
      output.error(output.API_ERROR, "Can't heal texture. Activate texture healer manipulator before");
      return;
    }

    const object = workspace.getObjectByID(objectID);
    if (!object || object.type !== objectType.FUSION) {
      output.error(output.API_ERROR, "Can't heal texture. Wrong object id: \"" + objectID + "\"");
      return;
    }

    const payload = null;// getFusionSelectionData(object, false, true);
    payload["objectUuid"] = object.getUUID();

    events.emit("healtexture", payload);
  },

  /**
   * Invert normals
   * @memberof WS
   * @method invertNormals
   */
  async invertNormals() {
    const objects = workspace.getObjects().filter(object => object.getVisibility() && !object.getLockState());
    const objectsPayload = objects.map(object => ({id: object.getID()}));
    events.emit("user_action", {type: msgType.INVERT_NORMALS, payload: {objects: objectsPayload}});
    await commonAPI.invertNormals(objects);
    events.emit("frame_requested");
  },

  /**
   * Enable or disable viewcube
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled Whether to enable viewcube or not
   * @method viewcubeSetEnabled
   */
  viewcubeSetEnabled({enabled}) {

    if (appState.getStateValue("showViewcube") === enabled) {
      return;
    }

    viewcube.setEnabled(enabled);
    appState.setStateValue("showViewcube", enabled);
    events.emit("frame_requested");

  },

  /**
   * Undo the last applied tool
   * @memberof WS
   * @method undo
   */
  undo() {
    history.schedulePreviousStepApply();
  },

  /**
   * Redo the last applied tool which was undone
   * @memberof WS
   * @method redo
   */
  redo() {
    history.scheduleNextStepApply();
  },

  //NOTE: it's a hack to know active object's id from ui
  getAllObjectsID() {
    const ids = [];
    for (const object of workspace.getObjects()) {
      ids.push(object.getID());
    }
    return ids;
  },

  /**
   * Enable or disable texture adjustment mode for selected fusion
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled Whether to enable adjustment or not
   * @param {String} params.objectID Fusion id
   * @method setTextureAdjusmentEnabled
   */
  setTextureAdjusmentEnabled({enabled, objectID = ""}) {

    const object = workspace.getObjectByID(objectID);

    if (enabled && !object) {
      output.error(output.API_ERROR, "Can't set texture adjustment. Wrong object id: \"" + objectID + "\"");
      return false;
    }

    if (object.type !== objectType.FUSION) {
      output.error(output.API_ERROR, "Can't set texture adjustment. Object is not a fusion.");
      return false;
    }

    if (object.model.shader.textureAdjustment === enabled) {
      return;
    }

    object.model.shader.setTextureHue(0);
    object.model.shader.setTextureSaturation(1);
    object.model.shader.setTextureBrightness(1);
    object.model.shader.setTextureContrast(1);
    object.model.shader.setTextureGamma(1);

    object.model.shader.setTextureAdjustmentEnabled(enabled);

    object.model.shader.setDirective("LIGHTING", !enabled && appState.getStateValue("lighting"));

    events.emit("frame_requested");

  },

  /**
   * Set texture adjustment options if this mode is enabled
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.hue New hue value
   * @param {Number} params.saturation New saturation value
   * @param {Number} params.brightness New brightness value
   * @param {Number} params.contrast New contrast value
   * @param {Number} params.gamma New gamma value
   * @method setTextureAdjusmentOptions
   */
  setTextureAdjusmentOptions(params) {

    let object = null;
    for (let obj of workspace.getObjects()) {
      if (obj.model.shader.textureAdjustment) {
        object = obj;
        break;
      }
    }

    if (!object) {
      output.error(output.API_ERROR, "Texture Adjusment is not enabled. Select the object first.");
      return;
    }

    let updated = false;
    const shader = object.model.shader;

    if ("hue" in params) {
      if (shader.getTextureHue() !== params.hue) {
        shader.setTextureHue(params.hue);
        updated = true;
      }
    }

    if ("saturation" in params) {
      if (shader.getTextureSaturation() !== params.saturation) {
        shader.setTextureSaturation(params.saturation);
        updated = true;
      }
    }

    if ("brightness" in params) {
      if (shader.getTextureBrightness() !== params.brightness) {
        shader.setTextureBrightness(params.brightness);
        updated = true;
      }
    }

    if ("contrast" in params) {
      if (shader.getTextureContrast() !== params.contrast) {
        shader.setTextureContrast(params.contrast);
        updated = true;
      }
    }

    if ("gamma" in params) {
      if (shader.getTextureGamma() !== params.gamma) {
        shader.setTextureGamma(params.gamma);
        updated = true;
      }
    }

    if (updated) {
      events.emit("frame_requested");
    }

  },

  /**
   * Bake current texture adjustment options
   * @memberof WS
   * @method applyTextureAdjusment
   */
  applyTextureAdjusment() {

    const object = workspace.getObjects().find(obj => obj.model.shader.textureAdjustment);

    if (!object) {
      output.error(output.API_ERROR, "Texture Adjustment is not enabled. Select the object first.");
      return;
    }

    const hue = object.model.shader.getTextureHue();
    const saturation = object.model.shader.getTextureSaturation();
    const brightness = object.model.shader.getTextureBrightness();
    const contrast = object.model.shader.getTextureContrast();
    const gamma = object.model.shader.getTextureGamma();

    const textureSlices = workspace.getFrameTextureSlices(object);
    if (textureSlices.length === 0) {
      output.error(output.API_ERROR, "Can't apply texture adjustment. Object doesn't have texture.");
      return;
    }

    const slicePath = textureSlices[0];
    //NOTE: we use the first texture for adjusment
    const index = 0;

    events.emit("user_action", {
      "type": msgType.ADJUST_TEXTURE,
      "payload": {
        "path": slicePath,
        "projectFiles": [],
        "textureSliceID": index,
        "objectID": object.getID(),
        "hue": hue,
        "saturation": saturation,
        "brightness": brightness,
        "contrast": contrast,
        "gamma": gamma,
      }
    });

    return textureAdjustment.apply(object.frames[0].model.textures[0], hue, saturation, brightness, contrast, gamma).then(() => {

      object.model.shader.setTextureHue(0);
      object.model.shader.setTextureSaturation(1);
      object.model.shader.setTextureBrightness(1);
      object.model.shader.setTextureContrast(1);
      object.model.shader.setTextureGamma(1);

    });

  },

  /**
   * Set objects' ordering. Cleanup previous set order
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array} params.ordering List of objects' ids to be ordered with numbers { order : 0, id : "n01" }
   * @method setOrdering
   */
  setOrdering(params) {

    const ordering = params.ordering.sort((item1, item2) => {return item1.order - item2.order});

    for (let i = 0; i < ordering.length - 1; ++i) {
      if (ordering[i].order === ordering[i + 1].order) {
        output.error(output.API_ERROR, "Couldn't set up the same order for two different objects.");
        return;
      }
    }

    const formattedOrdering = ordering.map(item => {return {id: item.id}});
    projectTree.setOrdering(formattedOrdering);
  },

  /**
   * Debug mode options.
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.colorizeChunks Whether each chunk should be painted in its own unique color or not
   * @param {HTMLElement} params.container Container used to print rendering info
   * @param {Boolean} params.showRenderingInfo Show rendering debug info or not
   * @param {Boolean} params.debugGPUTime Calculate GPU time query or not
   * @method setDebugMode
   */
  setDebugMode(params) {

    if ("colorizeChunks" in params) {

      const objects = workspace.getObjects();

      for (let object of objects) {
        const shader = object.model.shader;
        shader.setDirective("DEBUG_CHUNKS", params.colorizeChunks);
      }

    }

    if ("showRenderingInfo" in params) {

      if (params.showRenderingInfo) {
        hud.enable();
      } else {
        hud.disable();
      }

    }

    if ("debugGPUTime" in params) {
      appState.setStateValue("GPUTimeDebugEnabled", params.debugGPUTime);
    }

    events.emit("frame_requested");

  },

  /**
   * Remove objects from workspace
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array(Object)} params.descriptors Descriptors of objects' to be removed
   * @param {String} descriptors[i].objectID Id of deleting or parent object
   * @param {String} descriptors[i].l1ID Id of deleting sub-object
   * @method removeObjects
   */
  async removeObjects(params) {
    const payload = await workspace.removeObjects(params.descriptors);
    photos.update();
    events.emit("user_action", {type: msgType.REMOVE_OBJECT, payload});
  },

  /**
   * Preview distance map
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.srcObjectID Source Fusion id
   * @param {String} params.dstObjectID Destination Fusion id
   * @param {Number} params.searchDistance Search distance
   * @returns {Object} data Distance Map data
   * @method previewDistanceMap
   */
  async previewDistanceMap({srcObjectID, dstObjectID, searchDistance }) {
    if (appState.getStateValue("activeTool") !== appState.TT_MEASUREMENTS) {
      output.error(output.API_ERROR, "Can't preview Distance Map. Activate measures manipulator before");
      return;
    }

    const srcObject = workspace.getObjectByID(srcObjectID);
    if (!srcObject || !srcObject.loaded) {
      output.error(output.API_ERROR, `Can't bake Distance Map. Source object with id: ${srcObjectID} is not exist or not loaded.`);
      return;
    }

    const dstObject = workspace.getObjectByID(dstObjectID);
    if (!dstObject || !dstObject.loaded) {
      output.error(output.API_ERROR, `Can't bake Distance Map. Destination object with id: ${dstObjectID} is not exist or not loaded.`);
      return;
    }

    if (srcObject.type !== objectType.FUSION || dstObject.type !== objectType.FUSION) {
      output.error(output.API_ERROR, "Can't preview Distance Map. Object type is unsupported");
      return;
    }

    return await measurementsManipulator.previewDM(srcObject, dstObject, searchDistance);
  },

  /**
   * Bake distance map
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.name Name of the new Distance Map
   * @param {String} params.srcObjectID Source Fusion id
   * @param {String} params.dstObjectID Destination Fusion or CAD id
   * @param {Number} params.searchDistance Search distance
   * @method bakeDistanceMap
   */
  async bakeDistanceMap({ name, srcObjectID, dstObjectID, searchDistance }) {

    if (appState.getStateValue("activeTool") !== appState.TT_MEASUREMENTS) {
      output.error(output.API_ERROR, "Can't bake Distance Map. Activate measures manipulator before");
      return;
    }

    const srcObject = workspace.getObjectByID(srcObjectID);
    if (!srcObject) {
      output.error(output.API_ERROR, `Can't bake Distance Map. Source object with id: ${srcObjectID} is not exist or not loaded.`);
      return;
    }

    const dstObject = workspace.getObjectByID(dstObjectID);
    if (!dstObject) {
      output.error(output.API_ERROR, `Can't bake Distance Map. Destination object with id: ${dstObjectID} is not exist or not loaded.`);
      return;
    }

    const payload = {
      "srcUuid": srcObject.getUUID(),
      "tgtUuid": dstObject.getUUID(),
      "name": name,
      "searchDistance": searchDistance,
      "slices": [],
      "projectFiles": [],
    };

    for (const object of [srcObject, dstObject]) {
      if (object.type === objectType.FUSION) {
        const frameProperty = workspace.getObjectFramesProperty(object)[0];
        const fullSlicesPaths = await workspace.getFrameGeometrySlices(frameProperty);
        payload["slices"] = payload["slices"].concat(fullSlicesPaths);
      } else { //object.type === objectType.CAD
        const slicePath = workspace.getCadSlice(object);
        payload["slices"].push(slicePath);
      }
    }
    workspace.getObjectFiles(srcObject, payload["projectFiles"]);
    workspace.getObjectFiles(dstObject, payload["projectFiles"]);

    output.warn("bakeDistanceMap api is not implemented yet");
    //events.emit("bakeDistanceMap", payload);
  },

  /**
   * Get histogram for current distance map
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.distance [-distance; distance] is a range of valid distances
   * @param {Number} params.histogramSize Number of histogram ticks in output histogram
   * @param {Number} params.curveSize Number of curve points in output curve
   * @returns {Object} data Histogram data
   * @param {Array} data.histogram Normalized histogram values
   * @param {Array} data.curve Normalized curve points
   * @param {Array} data.ticks [mean - stdDeviation, mean, mean + stdDeviation]
   */
  calculateDistanceMapHistogram({distance, histogramSize, curveSize}) {
    return measurements.calculateDistanceMapHistogram(distance, histogramSize, curveSize);
  },

  /**
   * Set rendering properties for current distance map
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array} params.colors List of colors in hex-format to fill distance intervals
   * @param {{r, g, b}} params.thrColor Threshold color in hex-format
   * @param {Number} params.distance [-distance; distance] is a range for distance colors
   * @param {Number} params.lowerThr [lowerThr; upperThr] is a normalized range for threshold color
   * @param {Number} params.upperThr [lowerThr; upperThr] is a normalized range for threshold color
   * @param {Boolean} params.continuousMode Whether interpolate colors or not
   * @method setDistanceMapRenderingProperties
   */
  setDistanceMapRenderingProperties({colors, thrColor, distance, upperThr, lowerThr, continuousMode}) {
    measurements.setDistanceMapRenderingProperties(colors, thrColor, distance, lowerThr, upperThr, continuousMode);
  },

  /**
   * Fit CAD into fusion
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Fusion id
   * @param {(FM_EXPAND|FM_EXACT|FM_ROBUST)} params.fittingMode Fitting mode
   * @param {(CP_PLANE|CP_SPHERE|CP_TORUS|CP_CYLINDER|CP_CONE)} params.primitiveType Primitive type
   * @param {Object} params.constraints Object with primitive fitting constraints
   * @method fitCAD
   */
  async fitCAD({ objectID, fittingMode, primitiveType, constraints }) {
    if (appState.getStateValue("activeTool") !== appState.TT_CADFITTER) {
      output.error(output.API_ERROR, "Can't fit CAD. Activate cadfitter manipulator before");
      return;
    }

    const object = workspace.getObjectByID(objectID);
    if (!object || object.type !== objectType.FUSION) {
      output.error(output.API_ERROR, "Can't fit CAD. Wrong object id: \"" + objectID + "\"");
      return;
    }

    const payload = await getFusionSelectionData(object);
    payload["fittingMode"] = fittingModeToString(fittingMode);
    payload["primitiveType"] = cadPrimitiveToString(primitiveType);
    payload["constraints"] = constraints;
    payload["objectUuid"] = object.getUUID();

    events.emit("fitCad", payload);
  },

  /**
   * Apply fit mask to fusion
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.objectID Fusion id
   * @method applyFitMask
   */
  async applyFitMask({objectID}) {
    if (appState.getStateValue("activeTool") !== appState.TT_CADFITTER) {
      output.error(output.API_ERROR, "Can't apply fit mask. Activate cadfitter manipulator before");
      return;
    }

    const object = workspace.getObjectByID(objectID);
    if (!object || object.type !== objectType.FUSION) {
      output.error(output.API_ERROR, "Can't apply fit mask. Wrong object id: \"" + objectID + "\"");
      return;
    }

    await cadFitterManipulator.applyFitMask(object);
  },

  setCadFitterPickingMode({enabled}) {
    appState.setStateValue("cadFitterPickingMode", enabled);
    cadFitterManipulator.resetPickingMode();
  },

  applyPickCad(payload) {
    cadFitterManipulator.applyPickCad(payload);
  },

  /**
   * Set posteffects enabled or not
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(PE_COLOR_BALANCE|PE_VIGNETTE|PE_GRAIN|PE_SHARPNESS)} params.effect Posteffect
   * @param {Boolean} params.enabled Whether posteffect should be enabled or not
   * @method setPostEffectsEnabled
   */
  setPostEffectsEnabled({effect, enabled}) {
    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const postEffectPass = mainRenderGraph.getPass(renderGraph.POSTEFFECT);

    postEffectPass.setEnabledEffect(effect, enabled);
    windowManager.applyToAllWindows(window => window.renderGraph.requestFrame(false));
  },

  /**
   * Set texture adjustment options if this mode is enabled
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {(PE_COLOR_BALANCE|PE_VIGNETTE|PE_GRAIN|PE_SHARPNESS)} params.effect Posteffect
   * @param {Number} params.lowR New value for low R
   * @param {Number} params.lowG New value for low G
   * @param {Number} params.lowB New value for low B
   * @param {Number} params.midR New value for middle R
   * @param {Number} params.midG New value for middle G
   * @param {Number} params.midB New value for middle B
   * @param {Number} params.highR New value for high R
   * @param {Number} params.highG New value for high G
   * @param {Number} params.highB New value for high B
   * @param {Number} params.strength Strength of effect
   * @param {Number} params.strength Strength of effect
   * @param {Number} params.darkness Darkness of effect
   * @param {Number} params.hardness Hardness of effect
   * @method setColorBalanceOptions
   */
  setPosteffectOptions(params) {

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const postEffectPass = mainRenderGraph.getPass(renderGraph.POSTEFFECT);
    const scenes = postEffectPass.getScenes();

    if (params.effect === postEffects.COLOR_BALANCE) {
      setupColorBalanceEffect(postEffectPass.getScreenQuad(scenes[0]), params);
    } else if (params.effect === postEffects.SHARPNESS) {
      setupSharpnessEffect(postEffectPass.getScreenQuad(scenes[1]), params);
    } else if (params.effect === postEffects.GRAIN) {
      setupGrainEffect(postEffectPass.getScreenQuad(scenes[2]), params);
    } else if (params.effect === postEffects.VIGNETTE) {
      setupVignetteEffect(postEffectPass.getScreenQuad(scenes[3]), params);
    } else {
      output.error(output.API_ERROR, "Incorrect effect ID.");
      return;
    }

    windowManager.applyToAllWindows(window => window.renderGraph.requestFrame(false));
  },

  /**
   * Add Custom History Step
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.uuid New step uuid
   * @param {Object} params.payload New step payload
   * @method addCustomHistoryStep
   */
  addCustomHistoryStep(params) {
    history.addStep(stepType.CUSTOM, params.payload, params.uuid);
  },

  /**
   * Remove Custom History Step
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.uuid Removing step uuid
   * @method removeCustomHistoryStep
   */
  removeCustomHistoryStep(params) {
    history.removeCustomStep(params.uuid);
  },

  /**
   * Enable a point pop-up event if the point is under cursor
   * @memberof WS
   * @method enablePointPopup
   */
  enablePointPopup(clientX, clientY) {
    if (appState.getStateValue("activeTool") === appState.TT_ALIGN) {
      const {screenX, screenY} = inputCollector.clientToPhysical(clientX, clientY);
      alignManipulator.enablePointPopup(screenX, screenY);
    }
  },

  async applyEditingStack({stack}) {
    for (const command of stack) {
      switch (command.type) {
        case msgType.ERASE:
          appState.setState(appState.GS_OCTREE, true);
          workspace.setObjectState(objState.OS_OCTREE, false);

          await commands.erase(command.payload, true);
          break;
        case msgType.SMOOTH:
          appState.setState(appState.GS_OCTREE, true);
          appState.setState(appState.GS_HALF_EDGE, true);
          workspace.setObjectState(objState.OS_OCTREE, false);
          workspace.setObjectState(objState.OS_HALF_EDGE, false);

          await commands.smooth(command.payload, true);
          break;
        default:
          output.error(output.API_ERROR, "Message type \"" + command.type + "\" is not supported in applyEditingStack method.");
      }
    }
  },

  /*
  //Note: this is debug method for serializing commands sending to nodejs
  flushCommandStack() {
    return JSON.stringify(commandStack);
  },
  */

  /**
   * Set selection of several measures or a components. Unset selection of other measures.
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array(Object)} params.selections Array of selection objects
   * @param {String} params.selections[i].measureID Measure id
   * @param {Number} params.selections[i].sectionIDx Section index (only for section groups)
   * @param {Number} params.selections[i].contourIDx Contour index (only for section groups)
   * @method selectMeasures(params)
   * @returns Promise() Fulfilled after selection done
   */
  async selectMeasures(params) {
    measurements.dropSelections();
    for (const selection of params.selections || []) {
      measurements.setMeasureSelection(selection.measureID, selection.sectionIDx, selection.contourIDx, true);
    }
    await measurements.update();
  },

  getObjectMaxError(params) {
    return workspace.getObjectMaxError(params.objectID);
  },

  /**
   * Set measurement type for measurements creation manipulator
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {MET_LINEAR_MEASURE|MET_GEODESIC_MEASURE|MET_SECTION|MET_DISTANCE_MAP} params.type Measurement type
   * @method setMeasurementType
   */
  setMeasurementType(params) {
    measurementsManipulator.changeMeasurementType(params.type);
  },

  /**
   * Save current measurement constructed in manipulator
   * @memberof WS
   * @method saveMeasurement
   */
  saveMeasurement() {
    measurementsManipulator.save();
  },

  /**
   * Set fitting screen area for photo view mode (in css coordinates)
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.x X coordinate of left top corner
   * @param {Number} params.y Y coordinate of left top corner
   * @param {Number} params.width Area width
   * @param {Number} params.height Area height
   * @method setPhotoFitArea
   */
  setPhotoFitArea({x, y, width, height}) {
    const fitArea = [
      x * screenUtils.getPixelRatio(),
      y * screenUtils.getPixelRatio(),
      width * screenUtils.getPixelRatio(),
      height * screenUtils.getPixelRatio(),
    ];

    appState.setStateValue("photoFitArea", fitArea);
    photos.updateFitArea();
  },

  /**
   * Enter, exit or change photo in photo mode
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String|null} params.photoGroupID Id of the photo set or null if it's needed to exit photo mode
   * @param {String} params.photoID Id of the photo (in the mentioned photo set) which should be viewed
   * @param {Boolean} params.instantly Disable animation. Optional
   * @method viewPhoto
   */
  viewPhoto({photoGroupID, photoID, instantly}) {
    photos.viewPhoto(photoGroupID, photoID, instantly);
  },

  /**
   * Set photo state: default, hovered, active
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String|null} params.photoGroupID Id of the photo set
   * @param {String} params.photoID Id of the photo (in the mentioned photo set)
   * @param {PS_DEFAULT|PS_HOVERED|PS_ACTIVE} params.state New photo state
   * @method setPhotoState
   */
  setPhotoState({photoGroupID, photoID, state}) {
    photos.setState(photoGroupID, photoID, state);
  },

  /**
   * Enable or disable transparent photo view mode
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Boolean} params.enabled Should be transparent mode enabled or disabled
   * @method setPhotoViewTransparencyEnabled
   */
  setPhotoViewTransparencyEnabled({enabled}) {
    photos.setTransparentModeEnabled(enabled);
  },

  /**
   * Get photoset photos' previews. Method should be called after photoset object loading
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.photoGroupID Id of the photo set
   * @returns {Object} Preview data. Object with keys: {String} photoID, and values: {HTMLImageElement} photoPreview
   * @method getPreviewData
   */
  getPreviewData({photoGroupID}) {
    const photoGroup = workspace.getPhotoGroupByID(photoGroupID);
    if (!photoGroup) {
      output.error(output.API_ERROR, "Can't get preview data. Wrong photo group id: \"" + photoGroupID + "\"");
      return;
    }
    return photoGroup.previewData;
  },

  /**
   * Get photo archive files
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {String} params.photoGroupID Id of the photo set
   * @param {Array(String)} params.photoIDs Ids of photos (in the mentioned photo set)
   * @returns {Array(Object)} photoFiles Information about annotation appearance
   * @param {String} photoFiles[i].file Filename
   * @param {Number} photoFiles[i].index Index of the photo in pngz archive
   * @method getPhotoFiles
   */
  getPhotoFiles({photoGroupID, photoIDs}) {
    const photoGroup = workspace.getPhotoGroupByID(photoGroupID);
    if (!photoGroup) {
      output.error(output.API_ERROR, "Can't get photo files. PhotoGroup is not found: " + photoGroupID);
      return null;
    }

    return photoGroup.getPhotoFiles(photoIDs);
  },

  /**
   * Set scan display mode
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {SCM_TRIANGLES|SCM_SURFELS_HIGH|SCM_SURFELS_MID} params.mode Scan display mode
   * @method setScanMode
   */
  async setScanMode({mode}) {
    if (appState.getStateValue("scanMode") === mode) return;
    appState.setStateValue("scanMode", mode);

    await workspace.updateScanMode(mode);

    _updateManipulators();
    synchronizeObjectsState();
    await workspace.startProjectLoading();
    await workspace.reloadObjects();
    events.emit("visibility_changed");
  },

};

function _updateManipulators() {
  switch (appState.getStateValue("activeTool")) {
    case appState.TT_TRANSFORM:
      transformManipulator.update();
      break;
    case appState.TT_POSITIONER:
      positionManipulator.update();
      break;
    case appState.TT_ALIGN: {
      const aligneeWindow = windowManager.getWindow(windowManager.ALIGNEE);
      const alignerWindow = windowManager.getWindow(windowManager.ALIGNER);
      const aligneeObjectIDs = _alignWindowToObjects.get(aligneeWindow);
      const alignerObjectIDs = _alignWindowToObjects.get(alignerWindow);
      if (aligneeObjectIDs) windowManager.setWindowObjects(aligneeObjectIDs.map(aligneeObjectsID => workspace.getObjectByID(aligneeObjectsID)), windowManager.ALIGNEE);
      if (alignerObjectIDs) windowManager.setWindowObjects(alignerObjectIDs.map(alignerObjectsID => workspace.getObjectByID(alignerObjectsID)), windowManager.ALIGNER);
      break;
    }
    default: {
      if (_mirroringState.enabled) {
        const workspaceBlendingScene = windowManager.getWindow(windowManager.MAIN).renderGraph.getPostBlendingInterfaceScene();
        mirror.preview(workspace.getObjects(), _mirroringState.point, _mirroringState.plane, workspaceBlendingScene, true);
      }
    }
  }
}

function animateCamera(camera, rect, worldMinmax) {

  const cameraControl = windowManager.getWindow(windowManager.MAIN).cameraControl;

  cameraControl.moveToMinmax(worldMinmax, rect, animation, () => {},
  () => {
    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
  },
  () => {
    windowManager.applyToAllWindows(window => window.bufferReader.dropBuffers());
    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
  });

}

function animateCameraToAnnotation(camera, annotation) {
  const object = workspace.getObjectByID(annotation.meta.parentID);
  const matTransform = object ? object.model.modelMatrix : mat4.create();
  const quatTransform = object ? object.model.quat : quat.create();
  const worldCameraTrans = vec3.transformMat4(vec3.create(), annotation.meta.cameraLocalPos, matTransform);
  const worldCameraQuat = quat.multiply(quat.create(), quatTransform, annotation.meta.cameraLocalQuat);
  //NOTE: camera.top is used for old annotations
  const cameraTop = annotation.meta.cameraTop ? annotation.meta.cameraTop : camera.top;

  const window = windowManager.getWindow(windowManager.MAIN);
  animation.type = easingType.IN_OUT_SINE;

  const currentTrans = vec3.create();
  const currentQuat = quat.create();
  const transDelta = vec3.sub(vec3.create(), worldCameraTrans, camera.trans);
  const startTrans = vec3.clone(camera.trans);
  const startQuat = quat.clone(camera.quat);
  const startTop = camera.top;

  animation.setOnUpdate((value) => {

    vec3.scaleAndAdd(currentTrans, startTrans, transDelta, value);
    quat.slerp(currentQuat, startQuat, worldCameraQuat, value);

    const height = startTop + value * (cameraTop - startTop);
    const width = height * camera.aspect;

    camera.top = height;
    camera.bottom = -height;
    camera.right = width;
    camera.left = -width;

    camera.setRotation(...currentQuat);
    window.cameraControl.setPosition(currentTrans);

    window.renderGraph.requestFrame();
  });

  const finalMove = () => {
    camera.setRotation(...worldCameraQuat);
    window.cameraControl.setPosition(worldCameraTrans);
    window.bufferReader.dropBuffers();

    camera.top = cameraTop;
    camera.bottom = -cameraTop;
    camera.right = cameraTop * camera.aspect;
    camera.left = -cameraTop * camera.aspect;

    events.emit("camera_move", camera);
    events.emit("visibility_changed");

    window.renderGraph.requestFrame();
  }

  animation.setOnFinish(() => {
    finalMove();
  });

  if (animation.checkBusy()) {
    finalMove();
  } else {
    animation.start(0, 1, 0.2);
  }
}

function frame(time) {
  const timings = commonFacadeFunctions.frame(time);
  if (!timings) return;

  monitoring.update(timings.t);

  if (bbox.processSizeChanges()) {
    events.emit("workspace_bounding_changed");
    events.emit("interface_bounding_changed");
  }

  workspace.updateObjectsStates();

  updateDrawQueriesSchedule();

  animation.update(timings.dt);

  alignManipulator.updateAnimation(timings.dt);

  grid.updateAnimation(timings.dt);

  history.apply();

  hud.update();

  memoryProfiler.update(timings.t);

  window.requestAnimationFrame(frame);

}

function initEvents(canvas) {
  events.on("resize", () => resize(canvas) );

  events.on("update", (state) => {

    if ("memoryUsage" in state) {

      const currentDecimationRatio = workspace.getScansDecimationRatio();

      const memoryRatio = state.memoryUsage / config.geometryMemoryLimit;

      if (memoryRatio > 1 && currentDecimationRatio > 0.01) {

        const newRatio = Math.max(currentDecimationRatio * 0.9, 0.01);
        workspace.setScansDecimationRatio(newRatio);

      }

    }

  });

  events.on("object_moved", (object) => {
    const model = object.model;
    events.emit("user_action", {type: msgType.SET_OBJECT_TRANSFORM,
                                            payload: { objectID: object.getID(),
                                                       trans: model.trans,
                                                       scale: model.scale,
                                                       quat: model.quat } });
  });

  events.on("shallow_object_update", (object) => {
    bbox.fastMinMaxRecalc(object);
    scheduleDrawQueries(false);
  });

  events.on("objects_updated", (objects) => {
    for (let object of objects) {
      bbox.notifyThatMinMaxChanged(object);
      workspace.applyState(object);
    }
    windowManager.applyToAllWindows(window => window.bufferReader.dropBuffers());
    events.emit("visibility_changed");
  });

  events.on("visibility_changed", () => {
    scheduleDrawQueries(true);
    pickObject();

    if (appState.getStateValue("annotationsEnabled")) {
      annotationsModule.updateAnnotations();
    }
  });

  events.on("workspace_bounding_changed", () => {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    mainWindow.bufferReader.dropBuffers();
  });

  events.on("interface_bounding_changed", () => {
    grid.update();
  });

  events.on("object_loaded", (object) => {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const colorID = mainWindow.bufferReader.intID(object.getID());

    object.model.shader.setObjectID(colorID);
    synchronizeObjectsState();
    windowManager.applyToAllWindows(window => window.bufferReader.dropBuffers());
    events.emit("visibility_changed");

  });

  events.on("camera_move", (camera) => {

    if (camera !== workspace.getScene().camera) {
      return;
    }

    appState.setStateValue("hoverOn", null);

    grid.update();
    measurements.updateScreenAppearance();

    scheduleDrawQueries(false);

    if (appState.getStateValue("annotationsEnabled")) {
      annotationsModule.updateAnnotations(false);
    }

  });

  events.on("update_grid", () => {
    grid.updateDepth();
  });

  events.on("object_removed", (object) => {
    annotationsModule.unparentAnnotations(object);
  });

  events.on("manipulator_start", () => {
    const selectionType = appState.getStateValue("selectionType");
    const tool = appState.getStateValue("activeTool");

    if (tool === appState.TT_ERASER || tool === appState.TT_SMOOTHER || tool === appState.TT_CADFITTER
        || tool === appState.TT_DEFEATURE || tool !== appState.TT_TEXTURE_HEALER) {
      if (selectionType !== appState.ST_NONE) {
        appState.setStateValue("hoverOn", null);
      }
    }

    if (tool === appState.TT_TRANSFORM) {
      appState.setStateValue("hoverOn", null);
    }
  });

  events.on("client_mouseup", () => {
    pickObject();
  });

  events.on("client_mousemove", ({screenX, screenY}) => {
    if (controlIsActive) {
      return;
    }

    pickObject();
    measurements.checkDistanceMapPoint(screenX, screenY);
    measurements.checkSectionAnalysisPoint(screenX, screenY);
  });

  events.on("control_zoom", (camera) => {
    if (camera !== workspace.getScene().camera) {
      return;
    }
    selectionWidgets.updateSphereRadius();
  });

  events.on("control_zoom_start", () => {
    controlIsActive = true;
    controlStateHandler();
  });

  events.on("control_zoom_end", () => {
    controlIsActive = false;
    controlStateHandler();
    events.emit("visibility_changed");
  });

  events.on("control_move_start", () => {
    controlIsActive = true;
    controlStateHandler();
  });

  events.on("control_move_end", () => {
    controlIsActive = false;
    controlStateHandler();
    events.emit("visibility_changed");
  });

  events.on("select", () => {
    events.emit("user_action", {type: msgType.SELECT});
  });

  events.on("erase", (payload) => {
    events.emit("user_action", {type: msgType.ERASE, payload: payload});
    //commandStack.push({type: msgType.ERASE, payload: payload});
  });

  events.on("smooth", (payload) => {
    events.emit("user_action", {type: msgType.SMOOTH, payload: payload});
    //commandStack.push({type: msgType.SMOOTH, payload: payload});
  });

  events.on("undo", (payload) => {
    events.emit("user_action", {type: msgType.UNDO, payload});
  });

  events.on("redo", (payload) => {
    events.emit("user_action", {type: msgType.REDO, payload});
  });

  events.on("manipulatorDisabled", () => {
    history.removeSelectionSteps();
    events.emit("user_action", {type: msgType.RESET_HISTORY});
  });

  events.on("geometry_selection_changed", ({geometry, isSelected}) => {

    if (isSelected) {

      if (_selectedGeometries.size == 0) {
        appState.setStateValue("hasSelectedGeometry", true);
      }

      _selectedGeometries.add(geometry);

    } else {

      _selectedGeometries.delete(geometry);

      if (_selectedGeometries.size == 0) {
        appState.setStateValue("hasSelectedGeometry", false);
      }

    }

  });

  events.on("object_visibility_change", () => {
    setBboxVisibility(appState.getStateValue("bboxVisibility"));

    if (appState.getStateValue("activeTool") === appState.TT_TRANSFORM) {
      transformManipulator.update();
    }

    windowManager.applyToAllWindows(window => window.bufferReader.dropBuffers());
    events.emit("visibility_changed");
    events.emit("interface_bounding_changed");
  });

  events.on("object_geometry_updated", ({object, state}) => {
    synchronizeShaderState(object, state);
  });

}

function setCameraMinmaxOptimalTransform(camera, minmax) {
  const cameraControl = windowManager.getWindow(windowManager.MAIN).cameraControl;

  const dist = cameraControl.calculateBBoxOptimalDistance(minmax);

  const target = vec3.fromValues(0.5 * (minmax[0] + minmax[3]),
                                 0.5 * (minmax[1] + minmax[4]),
                                 0.5 * (minmax[2] + minmax[5]));
  const viewView = vec3.fromValues(0, 0, 1);
  const viewWorld = vec3.transformQuat(viewView, viewView, cameraControl.initialCameraQuat);
  const offset = vec3.scale(viewWorld, viewWorld, dist);
  const position = vec3.add(offset, target, offset);

  camera.copyTranslation(position);
  camera.copyRotation(cameraControl.initialCameraQuat);

}

function pushPreviewTask(object, width, height, useActiveCamera, resolve) {
  _objectPreviewTasks.push({object, width, height, useActiveCamera, resolve});
}

function popPreviewTask() {

  if (_activePreviewTask !== null || _objectPreviewTasks.length == 0) {
    return;
  }

  _activePreviewTask = _objectPreviewTasks.pop();

  const {object, width, height, useActiveCamera, resolve} = _activePreviewTask;

  drawObjectImage(object, width, height, useActiveCamera).then((pixels) => {

    colorUtils.flipImageBufferVertically(pixels, width, height);

    resolve(pixels);

    _activePreviewTask = null;

    popPreviewTask();

  });

}

function drawObjectImage(object, width, height, useActiveCamera) {

  const model = object.model;
  const oldOpacity = model.getOpacity();
  const isVisible = object.getVisibility();

  model.setOpacity(1.0);
  model.setVisibility(true);
  for (let i = 0; i < object.frames.length; ++i) {
    const frame = object.frames[i];
    if (appState.getStateValue("colorMode") === appState.CM_TEXTURE) {
      if (frame.textureList.length) {
        frame.setVisibility(true);
      }
    } else {
      frame.setVisibility(true);
    }
  }
  workspace.scheduleObjectDrawQueries(object, false);

  const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  const workspacePass = mainRenderGraph.getPass(renderGraph.MAIN);
  const mainScene = workspacePass.getMainScene();

  const previewPass = mainRenderGraph.getPass(renderGraph.READ_COLOR);
  const previewScene = previewPass.getMainScene();

  if (useActiveCamera) {
    previewScene.camera.copyTranslation(mainScene.camera.trans);
    previewScene.camera.copyRotation(mainScene.camera.quat);
  } else {
    setCameraMinmaxOptimalTransform(previewScene.camera, object.getWorldMinmax());
  }

  previewScene.removeChildren();
  previewScene.add(model);
  previewScene.resize(width, height);

  const promise = mainRenderGraph.readPixels(renderGraph.READ_COLOR, 0);

  mainRenderGraph.setEnabled(renderGraph.READ_COLOR, true);
  renderer.renderScene(previewScene);
  mainRenderGraph.setEnabled(renderGraph.READ_COLOR, false);

  model.setOpacity(oldOpacity);
  model.setVisibility(isVisible);
  for (let i = 0; i < object.frames.length; ++i) {
    const frame = object.frames[i];
    if (appState.getStateValue("colorMode") === appState.CM_TEXTURE) {
      if (frame.textureList.length) {
        frame.setVisibility(isVisible);
      }
    } else {
      frame.setVisibility(isVisible);
    }
  }

  workspace.scheduleObjectDrawQueries(object, true);

  return promise;

}

function getManipulator(type) {

  switch (type) {
  case appState.TT_VIEW:
  case appState.TT_LOD:
    return viewManipulator;
  case appState.TT_ALIGN:
    return alignManipulator;
  case appState.TT_ERASER:
    return eraseManipulator;
  case appState.TT_CADFITTER:
    return cadFitterManipulator;
  case appState.TT_CADPICKER:
    return cadPickerManipulator;
  case appState.TT_SMOOTHER:
    return smoothManipulator;
  case appState.TT_TRANSFORM:
    return transformManipulator;
  case appState.TT_POSITIONER:
    return positionManipulator;
  case appState.TT_DEFEATURE:
    return defeatureManipulator;
  case appState.TT_TEXTURE_HEALER:
    return textureHealerManipulator;
  case appState.TT_MEASUREMENTS:
    return measurementsManipulator;
  default:
    return null;
  }

}

function resize(canvas) {
  commonFacadeFunctions.resize(canvas);

  const { width, height } = screenUtils.getCanvasPhisycalSize(canvas);
  const size = {width, height};

  selectionWidgets.resize(width);
  measurements.updateScreenAppearance();
  events.emit("visibility_changed");
  events.emit("canvas_resize", size);
}

function wireframeSetColor(color) {

  const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  const wireframePass = mainRenderGraph.getPass(renderGraph.WIREFRAME);
  const wireFrameScene = wireframePass.getMainScene();
  const primitive = wireFrameScene._children[0];
  const shader = primitive.shader;
  shader.setColor(color.r, color.g, color.b);

  appState.setStateValue("wireframeColor", {"r": color.r, "g": color.g, "b": color.b});

}

function synchronizeApplicationState() {
  commonFacadeFunctions.synchronizeApplicationState();

  const colorMode = appState.getStateValue("colorMode");
  const backFaceMode = appState.getStateValue("backFaceMode");

  {
    const transparentBackfaceEnabled = backFaceMode === appState.BM_TRANSPARENT;
    windowManager.applyToAllWindows((window) => {
      const aaPass = window.renderGraph.getPass(renderGraph.ANTIALIASING);
      aaPass.setMaskedModeEnabled(transparentBackfaceEnabled);
    });
  }

  {
    const xRayEnabled = colorMode === appState.CM_XRAY;
    const color = xRayEnabled ? config.xrayBackgroundColor: config.backgroundColor;

    windowManager.applyToAllWindows((window) => {
      window.setBackgroundColor(color.r, color.g, color.b, 0);
    });
  }
}

function synchronizeObjectsState() {
  commonFacadeFunctions.synchronizeObjectsState();

  const renderMode = appState.getStateValue("renderMode");
  const colorMode = appState.getStateValue("colorMode");
  const filteringMode = appState.getStateValue("surfelsFilteringMode");

  let minSize = Infinity;

  const promises = [];
  for (const object of workspace.getObjects()) {

    const shader = object.model.shader;

    {
      const xRayEnabled = colorMode === appState.CM_XRAY;
      shader.setXrayEdgeFalloff(appState.getStateValue("xrayEdgeFalloff"));
      shader.setXrayIntensity(appState.getStateValue("xrayIntensity"));
      shader.setXrayEnabled(xRayEnabled);
    }

    {
      shader.setDirective("LIGHTING", appState.getStateValue("lighting") && !shader.textureAdjustment);
      if (object.type !== objectType.CAD) {
        shader.setBackfaceMode(appState.getStateValue("backFaceMode"));
      }
    }

    {
      if (object.type === objectType.SURFELS) {
        const disableFiltering = config.adaptiveSurfelsFiltering && controlIsActive || colorMode === appState.CM_XRAY ||
            object.model.getOpacity() !== 1.0;
        const filter = disableFiltering ? appState.SM_NONE : appState.getStateValue("surfelsFilteringMode");
        shader.setSurfelsFiltering(filter);

        shader.setDirective("SURFELS_BUFFERED_SIZE", renderMode != appState.RM_POINTS);
        shader.setPointSize(screenUtils.getPixelRatio() * config.surfelsFixedPointSize);
        shader.setSurfelsNoiseSize(appState.getStateValue("surfelsNoiseSize"));

        const xRayEnabled = colorMode === appState.CM_XRAY;
        const blendedSurfels = object.type === objectType.SURFELS && (filteringMode === appState.SM_BLEND &&
            config.adaptiveSurfelsFiltering && !controlIsActive);

        object.model.setTransparencyEnabled(xRayEnabled || blendedSurfels || object.model.properties.diffuseColor[3] < 1);

        minSize = Math.min(minSize, minmaxUtils.getDiagonal(object.getLocalMinmax()));
      }
    }

    {
      if (object.type !== objectType.CAD) {
        if (colorMode === appState.CM_SCAN_COLOR) {
          object.applyObjectColorToFrames();
        }
        if (colorMode === appState.CM_FRAME_COLOR) {
          object.applyUniqueColorToFrames();
        }
        if (colorMode === appState.CM_MAX_ERROR) {
          object.applyMaxErrorColorToFrames();
        }
        preview.updatePreview(object);
      }
    }

    promises.push(workspace.applyState(object));
  }

  for (const object of workspace.getObjects().filter((object) => object.type === objectType.SURFELS)) {
    object.model.shader.setObjectSize(minSize);
  }

  return Promise.all(promises).then(() => {
    normals.update();
  });

}

function synchronizeShaderState(object, state) {
  commonFacadeFunctions.synchronizeShaderState(object);

  const renderMode = appState.getStateValue("renderMode");
  const shadingType = appState.getStateValue("shadingType");

  const shader = object.model.shader;

  if (state & objState.OS_OCTREE) {
    object.model.setSelectionEnabled(appState.getState(appState.GS_OCTREE));
  }

  {
    const normalsAvailable = state & objState.OS_NORMAL || object.type === objectType.SURFELS;
    const smooth = object.type === objectType.SURFELS || renderMode === appState.RM_POINTS || shadingType === appState.SHT_SMOOTH;
    const shading = normalsAvailable && smooth ? appState.SHT_SMOOTH : appState.SHT_FLAT;

    shader.setShading(shading);
  }

  windowManager.applyToAllWindows(window => window.bufferReader.dropBuffers());
  windowManager.updateWindows();
}

async function pickObject() {
  const xy = inputCollector.getCoords();
  const screenX = xy[0];
  const screenY = xy[1];

  const mainWindow = windowManager.getWindow(windowManager.MAIN);

  if (mainWindow.isInViewport(screenX, screenY)) {

    const textureX = mainWindow.toViewportX(screenX);
    const textureY = mainWindow.toViewportY(screenY);

    const pickID = await mainWindow.bufferReader.readPixelCache(textureX, textureY, BufferReaderType.OBJECT_ID);

    appState.setStateValue("hoverOn", pickID);

  }

}

function getMaxAvailableMemory() {
  return performance.memory ? 0.5 * performance.memory.jsHeapSizeLimit : defaultMaxHeapBytesize;
}

function checkNeedsDepth(toolType) {
  return toolType === appState.TT_ERASER || toolType === appState.TT_SMOOTHER ||
    toolType === appState.TT_DEFEATURE || toolType === appState.TT_TEXTURE_HEALER ||
    toolType === appState.TT_ALIGN || toolType === appState.TT_CADFITTER || toolType === appState.TT_MEASUREMENTS ||
    toolType === appState.TT_POSITIONER || appState.getStateValue("annotationsEnabled");
}

function updateGeneralPurposeBufferUsage() {
  const toolType = appState.getStateValue("activeTool");

  const distanceMapEnabled = appState.getStateValue("distanceMapMode");
  const depthEnabled = checkNeedsDepth(toolType) || config.viewOnlyMode;
  if (distanceMapEnabled && depthEnabled) {
    output.error(output.DEVELOPERS_ERROR, "General purpose buffer requested twice");
    return;
  }

  appState.setState(appState.GS_DRAW_DEPTH, depthEnabled);
  workspace.setDrawDepth(depthEnabled);
  workspace.setWriteDistanceMap(distanceMapEnabled);

  const needsGeneralPurposeBuffer = distanceMapEnabled || depthEnabled;

  windowManager.applyToAllWindows(function(window) {
    const mainPass = window.renderGraph.getPass(renderGraph.MAIN);
    mainPass.enableGeneralPurposeTexture = needsGeneralPurposeBuffer;
    mainPass.update();
  });
}

function setBboxVisibility(isVisible) {
  appState.setStateValue("bboxVisibility", isVisible);
  const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  const workspaceInterfaceScene = mainRenderGraph.getInterfaceScene();
  bbox.setVisibility(workspace.getObjects(), isVisible, workspaceInterfaceScene);
}

function scheduleDrawQueries(shouldRun) {
  _drawQueriesState = shouldRun ? DRAW_QUERIES_SHOULD_RUN: DRAW_QUERIES_SHOULD_STOP;
}

function updateDrawQueriesSchedule() {

  if (_drawQueriesState === DRAW_QUERIES_FINE) {
    return;
  }

  //NOTE: we do not request to drop query results if we've done so
  // during the previous update.
  if (_drawQueriesState === DRAW_QUERIES_SHOULD_STOP &&
      _prevQueriesState === _drawQueriesState) {
    return;
  }

  const shouldRun = _drawQueriesState === DRAW_QUERIES_SHOULD_RUN;
  const objects = workspace.getObjects();

  for (let object of objects) {
    workspace.scheduleObjectDrawQueries(object, shouldRun);
  }

  _prevQueriesState = _drawQueriesState;
  _drawQueriesState = DRAW_QUERIES_FINE;

}

const setupColorBalanceEffect = (function() {

  const vec3Tmp = vec3.create();
  const vec3Tmp2 = vec3.create();
  const vec3Tmp3 = vec3.create();

  return function(screenQuad, params) {

    const low = screenQuad.shader.getBalanceLow(vec3Tmp);
    const mid = screenQuad.shader.getBalanceLow(vec3Tmp2);
    const high = screenQuad.shader.getBalanceLow(vec3Tmp3);

    if ("lowR" in params) {
      low[0] = params.lowR;
    }
    if ("lowG" in params) {
      low[1] = params.lowG;
    }
    if ("lowB" in params) {
      low[2] = params.lowB;
    }

    if ("midR" in params) {
      mid[0] = params.midR;
    }
    if ("midG" in params) {
      mid[1] = params.midG;
    }
    if ("midB" in params) {
      mid[2] = params.midB;
    }

    if ("highR" in params) {
      high[0] = params.highR;
    }
    if ("highG" in params) {
      high[1] = params.highG;
    }
    if ("highB" in params) {
      high[2] = params.highB;
    }

    screenQuad.shader.setBalanceLow(low);
    screenQuad.shader.setBalanceMid(mid);
    screenQuad.shader.setBalanceHigh(high);

  }

})();

const setupSharpnessEffect = (function() {

  return function(screenQuad, params) {
    if ("strength" in params) {
      screenQuad.shader.setStrength(params.strength);
    }
  }

})();

const setupGrainEffect = (function() {

  return function(screenQuad, params) {
    if ("strength" in params) {
      screenQuad.shader.setStrength(params.strength);
    }
  }

})();

const setupVignetteEffect = (function() {

  return function(screenQuad, params) {
    if ("darkness" in params) {
      screenQuad.shader.setDarkness(params.darkness);
    }

    if ("hardness" in params) {
      screenQuad.shader.setHardness(params.hardness);
    }
  }

})();

const hideNonSelectedObjects = (function() {

  const hideTransparency = config.postBlending ? 0.1 : 0.03;
  const savedTransparencies = new WeakMap();

  return function(selectedObject) {
    for (const object of workspace.getObjects()) {
      if (savedTransparencies.has(object)) {
        const savedTransparency = savedTransparencies.get(object);
        object.model.shader.setMonochromaticEnabled(false);
        object.setOpacity(savedTransparency);
        savedTransparencies.delete(object);
      }
    }

    if (!selectedObject || !selectedObject.getVisibility()) {
      return;
    }

    for (const object of workspace.getObjects()) {
      if (object === selectedObject) {
        continue;
      }

      savedTransparencies.set(object, object.model.getOpacity());
      object.model.shader.setMonochromaticEnabled(true);
      object.setOpacity(hideTransparency);
    }
  }

})();

async function getFusionSelectionData(object) {
  const selectionsData = {
    "selections": [],
    "slices": [],
    "projectFiles": [],
  };

  for (const chunk of object.frames[0].model.chunks) {

    if (chunk.hasSelection()) {

      const geometry = chunk.geometry;
      const selectionMask = geometry.getItemBuffer("a_selection_mask");

      const chunkId = chunk.getID();
      const selectionData = {
        "chunkID": chunkId,
        "selectionMask": selectionMask.slice(),
      };

      selectionsData["selections"].push(selectionData);
    }
  }

  const frameProperty = workspace.getObjectFramesProperty(object)[0];
  const fullSlicesPaths = await workspace.getFrameGeometrySlices(frameProperty);
  selectionsData["slices"] = fullSlicesPaths;
  workspace.getObjectFiles(object, selectionsData["projectFiles"]);

  return selectionsData;

}

function fittingModeToString(fittingMode) {
  switch (fittingMode) {
    case WS.FM_EXACT:
      return "exact";
    case WS.FM_EXPAND:
      return "expand";
    case WS.FM_ROBUST:
      return "robust";
    default:
      output.error(output.DEVELOPERS_ERROR, "unknown fitting mode: " + fittingMode);
  }
}

function cadPrimitiveToString(cadPrimitive) {
  switch (cadPrimitive) {
    case WS.CP_PLANE:
      return "plane";
    case WS.CP_CONE:
      return "cone";
    case WS.CP_CYLINDER:
      return "cylinder";
    case WS.CP_SPHERE:
      return "sphere";
    case WS.CP_TORUS:
      return "torus";
    default:
      output.error(output.DEVELOPERS_ERROR, "unknown cad primitive: " + cadPrimitive);
  }
}

function getRectNDC(rect = null) {
  const rectNDC = {
    minx : -1.0,
    miny : -1.0,
    maxx : 1.0,
    maxy : 1.0,
  };

  if (rect) {
    const mainWindow = windowManager.getWindow(windowManager.ALIGNER);
    const camera = mainWindow.renderGraph.getWorkspaceScene().camera;

    const minNDC = screenUtils.clientToScreen(mainWindow.getViewport(), camera.viewport, rect.minx, rect.miny);
    const maxNDC = screenUtils.clientToScreen(mainWindow.getViewport(), camera.viewport, rect.maxx, rect.maxy);

    rectNDC.minx = Math.min(minNDC.x, maxNDC.x);
    rectNDC.miny = Math.min(minNDC.y, maxNDC.y);
    rectNDC.maxx = Math.max(minNDC.x, maxNDC.x);
    rectNDC.maxy = Math.max(minNDC.y, maxNDC.y);
  }

  return rectNDC;
}

function _updateDataStructuresUsage() {
  const toolType = appState.getStateValue("activeTool");
  const octreeNeeded = toolType === appState.TT_ERASER || toolType === appState.TT_CADFITTER || toolType === appState.TT_DEFEATURE || toolType === appState.TT_TEXTURE_HEALER || toolType === appState.TT_SMOOTHER || measurements.isAnySectionVisible();
  const halfEdgesNeeded = toolType === appState.TT_SMOOTHER;
  appState.setState(appState.GS_OCTREE, octreeNeeded);
  appState.setState(appState.GS_HALF_EDGE, halfEdgesNeeded);
}

function controlStateHandler() {
  if (config.adaptiveSurfelsFiltering) {
    synchronizeObjectsState();
    windowManager.applyToAllWindows(window => window.updateObjectsScene());
  }
}

const WS = {...commonFacadeAPI, ...WS_DERIVED};
decorator.applyDecoratorToWholeModule(decorator.logArguments, WS, ["setPhotoState"]);

export default WS;

if (process.env.NODE_ENV === "development") {
  window.WS = WS;
}
